// Type-only — a change request's frozen card snapshot lives on Doc.scope. Erased
// at runtime, so change-request.ts importing back from here makes no real cycle.
import type { ChangeRequest } from './change-request';
// Type-only — the idle-nudge module owns the IdleWindows shape; importing it here
// is erased at runtime, so idle-nudge.ts importing back for Issue makes no real cycle.
import type { IdleWindows } from './idle-nudge';
// Type-only — the checklist-prompts module owns the ChecklistPrompts shape.
import type { ChecklistPrompts } from './checklist-prompts';

export type IssueStatus = 'backlog' | 'todo' | 'inprogress' | 'staging' | 'inreview' | 'done' | 'wontdo';
export type IssuePriority = 'critical' | 'high' | 'medium' | 'low';

/**
 * HOW ESSENTIAL A REQUIREMENT IS FOR THE RELEASE — the MoSCoW scale, and a
 * DIFFERENT question from `priority`. Priority is how URGENT a card is; this is
 * whether the release can ship without it: a Must-have is committed, a Could-have
 * goes if time runs short. A card carries both because "urgent" and "essential"
 * are not the same — an urgent Could-have and an unhurried Must-have both exist.
 * The person sets it; it is never inferred. Absent until someone decides it.
 */
export type MoscowLevel = 'must' | 'should' | 'could' | 'wont';
/*
 * WHAT KIND OF WORK A CARD IS.
 *
 * Widened from task and bug on 23 August 2026 for #195: CPFC's change request
 * form writes each item as "New Feature" or "Improvement", and that form is
 * GENERATED from the board — so those have to be things a card can be, rather
 * than a second classification living inside the document where nobody could
 * correct it.
 */
/**
 * ⚠️ A SLUG, NOT A FIXED WORD, SINCE #302. The board names its own types; this
 * holds whichever one a card was given, and it never changes once written.
 *
 * It stayed `string` rather than becoming a foreign key because `kind` is read
 * by seventy files: rewriting it would have meant changing all of them at once,
 * backfilling every board, and having no way back if any of it were wrong. It
 * did not have to change. `src/issue-types.ts` carries the name and the meaning
 * against the slug, so a card that said `bug` in June still says `bug` and the
 * board decides what that is called and what it counts as.
 *
 * The four below are what every board that existed before #302 has, and what
 * every tracker file may always say — see `resolveType`. A board made today
 * starts with Vinnie's reporting words instead: Feature, Bug, Tech debt,
 * Maintenance.
 */
export type IssueKind = string;

/** The four originals, for the fallbacks that still name them one by one. */
export type OriginalKind = 'task' | 'bug' | 'improvement' | 'feature';

/**
 * Roughly how much work something is.
 *
 * Priority says how much it matters; this says how much it costs. Without it a
 * board cannot tell a two-hour job from a two-week one, so among equally urgent
 * work it can only fall back to whichever was touched most recently — which is
 * not a reason to do anything.
 *
 * Deliberately three coarse options. Anything finer invites arguing about
 * estimates instead of doing the work, and a manual team will not keep it up.
 */
export type IssueSize = 'quick' | 'medium' | 'long';

/* RUN vs CHANGE — the investment class of a card (#435). Change = building/investment,
   Run = keeping the service running (ad-hoc fixes + upkeep). DERIVED from the card's
   type (its Means), so this field holds ONLY a manual override and is absent (null)
   whenever the card follows its type — mirrors how `area`/`moscow` carry a value only
   once set. Read the EFFECTIVE class through `investmentClassOf` (src/investment.ts),
   never this field alone. */
export type InvestmentClass = 'change' | 'run';

/* COST BASIS — how a project's vendor(s) are paid, which decides how its cost side
   can be read. 'fixed' = fixed price (AC follows the payment schedule, so CPI tracks
   timing not effort); 'tm' = time & materials (spend tracks effort, CPI is a genuine
   efficiency read); 'mixed' = both, or unknown — the balanced read. A project-level
   attribute on ProjectMeta; see src/portfolio.ts costBasisRead for how each shape
   tunes the cost tile. */
export type CostBasis = 'fixed' | 'tm' | 'mixed';

/**
 * The kinds a card can be, in the order the picker offers them, and what each
 * is called on screen. The stored word is never shown — "New feature" reads as
 * a thing somebody asked for; `feature` reads as a database value.
 */
/**
 * ⚠️ THE LAST RESORT, NOT THE ANSWER — read `src/issue-types.ts` instead.
 *
 * Kept because a board's types arrive over the network and these two are what
 * the first paint has to draw. A card rendered against them looks exactly as it
 * did before #302, which is the right thing to show for the half-second before
 * the truth lands. Anything that reads these where the board's own types are
 * available is a bug: it will show "New feature" on a board that calls it
 * "Enhancement".
 */
export const KIND_ORDER: OriginalKind[] = ['task', 'bug', 'improvement', 'feature'];

export const KIND_LABEL: Record<string, string> = {
  task: 'Task',
  bug: 'Bug',
  improvement: 'Improvement',
  feature: 'New feature',
};

export const SIZE_ORDER: IssueSize[] = ['quick', 'medium', 'long'];

export const SIZE_LABEL: Record<IssueSize, string> = {
  quick: 'Quick',
  medium: 'A few days',
  long: 'Long',
};

export const SIZE_HINT: Record<IssueSize, string> = {
  quick: 'Hours, or a day. Build it and it is done.',
  medium: 'Several days of work.',
  long: 'A week or more, or it needs breaking up.',
};

/**
 * THE NUMBER BEHIND EACH WORD — added 15 August 2026, on Vinnie's decision.
 *
 * Stage 3 of *Working in sprints* asked a question that had to be answered
 * before a burndown could exist: three words do not add up, so a chart drawn
 * from them has nothing to plot. Either the words get numbers or the whole
 * stage becomes a capacity conversation. He chose the numbers.
 *
 * THE WORDS ARE STILL THE INTERFACE. Nobody is asked for a number anywhere —
 * the size selector says Quick, A few days, Long exactly as it did, and the
 * comment above about not inviting arguments over estimates still stands. The
 * numbers sit behind them so that a fortnight's work can be added up. Fifteen
 * cards is not an amount of work; 15 points is.
 *
 * WHY 1, 3, 8 AND NOT 1, 2, 3. The gaps grow because the uncertainty grows.
 * "Long" is not three times "quick" — it is the one that might be a fortnight,
 * and spacing it evenly says the estimate is more precise than anybody's
 * estimate of a long job has ever been. Widely spaced numbers also stop a
 * sprint being filled with a rounding error: eight quicks and one long are
 * plainly not the same fortnight, where 3 × 3 and 9 × 1 would read as equal.
 *
 * WHAT THESE ARE NOT. Not hours, and nothing in the app converts them to hours
 * or to days. They are only comparable to each other and only useful summed —
 * a team's velocity is whatever this board's numbers add up to per fortnight,
 * which is why a velocity from one board says nothing about another.
 */
export const SIZE_POINTS: Record<IssueSize, number> = {
  quick: 1,
  medium: 3,
  long: 8,
};

/**
 * What kind of thing a blocked item is waiting for.
 *
 * This is the one field the whole "what would unblock the most work" model
 * rests on. It is deliberately generic — a phone build, a marketing site and
 * an accounting system all block on these same nine things — because reading
 * it out of the wording only ever worked for a tracker written by one author
 * in one house style.
 */
export type BlockerType =
  | 'item'      // another item on this board — the only kind that names a target
  | 'person'    // a named person has to do something
  | 'decision'  // a call has to be made
  | 'external'  // someone outside the team: client, vendor, legal
  | 'test'      // a test, measurement or real-world check
  | 'design'    // has to be designed or planned before it can be built
  | 'bundled'   // deliberately riding along with other work
  | 'parked'    // set aside on purpose, nothing is stopping it
  | 'other';

/**
 * ONE THING BLOCKING A CARD — and a card can have several (#456).
 *
 * The scalar `blocked` / `blockerType` / `blockerTarget` held exactly one
 * blocker and named its target as a bare string. `blockerRefs` (on Issue, jsonb
 * column `blocker_refs`, migration 0157) holds the real shape: a LIST of
 * entries, each with its own kind, the words someone typed, and the cards or
 * people it names — so "blocked by #142 AND awaiting Jo" is two linked entries,
 * not one string.
 *
 * A `ref` links to a live card (its status is read fresh, never frozen) or to a
 * person on the board (who is told their input is being waited on — #456). The
 * `label` is the display name captured when the entry was made, so the chip
 * reads even before the linked row loads; the link itself resolves by `ref`.
 */
/**
 * THE FOUR MS-PROJECT DEPENDENCY LINK TYPES, carried on a card-kind blocker so a
 * dependency can be more than the implicit finish-to-start. `FS` (the default and
 * the overwhelming common case) = the predecessor must FINISH before this starts;
 * `SS` = they can start together once the predecessor has started; `FF` = this
 * can't finish until the predecessor finishes; `SF` = this can't finish until the
 * predecessor starts. Absent reads as `FS`, so every link written before this (and
 * every one where nobody picks a type) behaves exactly as it does today.
 */
export type DependencyLinkType = 'FS' | 'SS' | 'FF' | 'SF';

export interface BlockerRef {
  kind: 'card' | 'person';
  /** A card reference (e.g. `#142`) or a person's identifier (email / id / name). */
  ref: string;
  /** Display label captured at set-time — the card title or the person's name. */
  label?: string;
  /**
   * CARD LINKS ONLY — the dependency's link type; absent reads as `FS`. Ignored on
   * a `person` ref (a person isn't a scheduled predecessor). Rides in the existing
   * `blocker_refs` jsonb, so it needs no column.
   */
  linkType?: DependencyLinkType;
  /**
   * CARD LINKS ONLY — lag (+) or lead (−) in WORKING days on the link: "finish, then
   * wait N working days" (+N) or "may begin N working days before" (−N). Absent or 0
   * = no lag. Counted on the board's own working calendar, never raw calendar days.
   */
  lag?: number;
}

export interface BlockerEntry {
  /** What kind of blocker this entry is — the same nine kinds as the scalar. */
  type: BlockerType;
  /** The words someone typed: what is being waited on, or why. */
  text: string;
  /** The cards and/or people this entry names (zero or more). */
  refs: BlockerRef[];
  /** When this entry was set — milliseconds. */
  at?: number;
  /**
   * Who recorded this entry — their address. Stamped at write time so the feed
   * can name the person doing the waiting ("Pat is waiting on you to unblock
   * FDD-142"). Read only by the notify trigger (migration 0159); never shown
   * raw on a card.
   */
  by?: string;
}

export const BLOCKER_TYPES: BlockerType[] = [
  'item', 'person', 'decision', 'external', 'test', 'design', 'bundled', 'parked', 'other',
];

export const BLOCKER_LABEL: Record<BlockerType, string> = {
  item: 'Another card on this board',
  person: 'A person needs to do something',
  decision: 'A decision needs making',
  external: 'Someone outside the team',
  test: 'A test, measurement or real-world check',
  design: 'It needs designing or planning first',
  bundled: 'Bundled with other work',
  parked: 'Parked on purpose',
  other: 'Something else',
};

/**
 * THE SHORT HEADER a blocker entry wears on the card (#456) — a few words that
 * name the kind at a glance, above its references and reason. Shorter than
 * BLOCKER_LABEL (which is the full sentence the kind picker shows): a header, not
 * a prompt. Vinnie chose the short form over the fuller picker phrasing (10 Oct).
 */
export const BLOCKER_HEAD: Record<BlockerType, string> = {
  item: 'Blocked by a card',
  person: 'Awaiting a person',
  decision: 'Awaiting a decision',
  external: 'Awaiting someone outside',
  test: 'Awaiting a check',
  design: 'Needs designing first',
  bundled: 'Bundled with other work',
  parked: 'Parked on purpose',
  other: 'Blocked',
};

/** What to ask for once a kind is chosen, and how to word the answer. */
export const BLOCKER_ASKS: Record<BlockerType, { label: string; hint: string; kind: 'issue' | 'person' | 'text' | 'none' }> = {
  item: { label: 'Which card?', hint: 'The one that has to finish first.', kind: 'issue' },
  person: { label: 'Who?', hint: 'The person it is waiting on.', kind: 'person' },
  decision: { label: 'What has to be decided?', hint: 'e.g. whether to keep the server', kind: 'text' },
  external: { label: 'Who outside the team?', hint: 'e.g. the client, legal, the vendor', kind: 'text' },
  test: { label: 'Which test or measurement?', hint: 'e.g. the load test', kind: 'text' },
  design: { label: 'What needs designing?', hint: 'Optional — a few words if it helps.', kind: 'text' },
  bundled: { label: 'Bundled with what?', hint: 'e.g. the next release', kind: 'text' },
  parked: { label: 'Picked up when?', hint: 'e.g. after the launch', kind: 'text' },
  other: { label: 'Waiting on what?', hint: 'A few words, so this is not a dead end.', kind: 'text' },
};

/** The heading Next up uses when this is the biggest group. */
export const BLOCKER_ACTION: Record<BlockerType, string> = {
  item: 'Finish what they depend on',
  person: 'Someone needs to act',
  decision: 'Make a call',
  external: 'Chase someone outside the team',
  test: 'Run a test or measurement',
  design: 'Design it first',
  bundled: 'Bundled with other work',
  parked: 'Parked on purpose',
  other: 'Waiting on something else',
};

export interface Comment {
  id: string;
  author: string;
  text: string;
  createdAt: number;
  /**
   * Taken off the card — hidden, not erased.
   *
   * Erasing would make "removed by mistake" a rescue rather than a click, and
   * the first time comments were tidied by hand it was only recoverable because
   * somebody thought to save the card first. The record stays; the screen stops
   * showing it.
   */
  removed?: boolean;
  removedBy?: string;
  removedAt?: number;
  /**
   * This one was sent to the person who reported the issue — it left the
   * building. Everything without it is a note between us.
   *
   * WRITTEN ONLY BY THE SERVER, never by the comment box. A browser that could
   * set this could decide what somebody in another workspace gets to read, so
   * the app shows the flag and never sets it: see supabase/functions/reply.
   */
  toReporter?: boolean;
  /**
   * THE ENTRY THIS IS A REPLY TO — the id of the status-log update (or the
   * comment) it threads under, so a comment can answer a specific update rather
   * than float free at the bottom of the card. Absent on a top-level comment.
   *
   * It points at an id that lives in EITHER store — a `StatusUpdate.id` in the
   * card's `standsLog` or another `Comment.id` — because the card shows the two
   * as one feed. Replies are one level deep: a reply never carries a parent of
   * its own.
   */
  parentId?: string;
  /**
   * THE ADDRESS OF WHOEVER WROTE THE ENTRY BEING REPLIED TO — carried so the
   * server can tell that person a reply landed, without having to resolve an id
   * that may live in the `standsLog` jsonb rather than the comments table. It is
   * a hint, not an authority: the notify function still checks membership and
   * never pings the replier themselves. Absent on a top-level comment.
   */
  parentAuthor?: string;
  /**
   * WHEN THE WORDS WERE LAST CHANGED, and by whom. Absent on a comment that has
   * never been edited. Set when the author (or an admin) rewrites the text —
   * the card shows a quiet "edited" mark, and the words are theirs to correct.
   * `editedBy` is who did it, which need not be the author: an admin may tidy an
   * abusive line rather than only remove it.
   */
  editedAt?: number;
  editedBy?: string;
}

/**
 * A LIGHT, QUIET REACTION on a card-feed entry — an emoji left on a comment or a
 * status update (migration 0143). It is a flat row, not nested in the issue: a
 * reaction is written by someone who does not own the thing reacted to, so it is
 * a row the REACTOR owns and needs no rights over the target.
 *
 * `targetId` is the id of whichever entry it sits on — a `Comment.id` or a
 * `StatusUpdate.id` in the card's `standsLog` — the same polymorphic bare id a
 * reply's `parentId` uses. One person leaves one of a given emoji on a given
 * entry; pressing it again takes it off, so (issueId, targetId, emoji, reactor)
 * identifies it with no id of its own. Raises no notification — lighter than a
 * Note.
 */
export interface Reaction {
  issueId: string;
  targetId: string;
  emoji: string;
  reactor: string;
  at: number;
}

export interface Activity {
  id: string;
  who: string;
  at: number;
  /**
   * WHERE THE CARD MOVED FROM AND TO — set only on a status change, absent on
   * an assignment, a comment or a title edit.
   *
   * BESIDE `what`, NOT INSTEAD OF IT. `what` is a sentence written for a person
   * reading the history; these two are for the charts, which cannot parse
   * English that the app is free to improve. See migration 0018 — recorded from
   * the day sprints started rather than the day they were finished, because
   * history not kept cannot be charted later.
   */
  fromStatus?: IssueStatus;
  toStatus?: IssueStatus;
  /**
   * WHICH SPRINT IT LEFT AND WHICH IT JOINED — the same shape as the two above,
   * on purpose, so whatever reads one reads the other without learning a second
   * convention. Empty string means the backlog, which is a real answer at both
   * ends: pulled in from nowhere, or dropped back out.
   *
   * SPILLOVER IS ONLY VISIBLE HERE. A card that was in last fortnight and is in
   * this one has, from the issue alone, always been in this one — nothing else
   * records that it was ever anywhere else. See migration 0019.
   */
  fromSprint?: string;
  toSprint?: string;
  /**
   * WHAT IT WAS SIZED AT BEFORE AND AFTER — the same shape again, so whatever
   * reads the other two reads this without learning a third convention.
   *
   * NOTHING READS THESE YET, and that is deliberate. Sizes carry numbers now
   * (see SIZE_POINTS), so a burndown is drawn from them — and today it uses
   * each card's size as it stands, which means re-sizing a card changes what
   * last Monday looked like. Recorded from now so the chart that fixes that has
   * history to read. Same argument as migration 0018, one stage later.
   *
   * An empty string is a real value: the size was REMOVED, which is work
   * quietly leaving every total on the board.
   */
  fromSize?: IssueSize | '';
  toSize?: IssueSize | '';
  what: string; // plain English, e.g. "changed status from todo to done"
  /**
   * What a long text field said before it was emptied or replaced.
   *
   * WHY IT IS KEPT AT ALL. Clearing a blocker wipes the written reason on every
   * issue in its group at once, and the board kept no copy. Twenty were cleared
   * by a single press on 5 August 2026; most were recoverable only because they
   * had come from an external tracker and the wording survived there. Two had
   * been written on the board and are simply gone.
   *
   * Recording it here costs a line and makes the same accident readable rather
   * than final. Only the long fields carry it — a status going from todo to done
   * already says both halves in `what`.
   */
  was?: string;
}

/**
 * A PART OF THE PRODUCT, named by the board rather than by us — #269.
 *
 * `kind` says what sort of work it is and is ours to define: task, bug,
 * improvement, feature are true of every project. What part of the product a
 * card touches is not ours to define, because TRINRE's parts are not
 * FlightDeck's. So this is a list a board keeps, not a union type.
 */
export interface Area {
  id: string;
  name: string;
  /**
   * ONE COLOUR FOR THE AREA, not one per screen. The same value draws the rail
   * on a board card, the chip in the backlog and the bar on Insights — which is
   * what lets somebody learn the colour once and then read it anywhere.
   */
  colour: string;
  position: number;
  /**
   * WHEN IT WAS RETIRED; absent or null means it is still live.
   *
   * Retiring is what "remove" does, by Vinnie's decision of 26 August 2026. The
   * area stops being offered on new work and keeps its place on every card that
   * already carries it, so a chart drawn in June still reads the same in
   * December. Anything counting by area must keep counting a retired one and
   * say "(retired)" beside it rather than dropping it, or last month's totals
   * silently shrink when somebody tidies the settings.
   */
  retiredAt?: number | null;
  /**
   * WHO IS ACCOUNTABLE for this part of the product — a person's account (email),
   * set on the area and stored (#462). Absent/null means none is set, and the
   * screens fall back to the derived plurality owner, labelled "auto"; it is
   * never a silent default. Distinct from an epic's owner, which is always
   * derived — a standing part of the estate has a standing lead, the way a Jira
   * component has a component lead.
   */
  lead?: string | null;
}

/** Only the ones still on offer. Retired areas stay readable everywhere else. */
export function liveAreas(areas: Area[]): Area[] {
  return areas.filter((a) => !a.retiredAt).sort((a, b) => a.position - b.position);
}

/**
 * A MANAGED LABEL — a board's cross-cutting tag (#462 follow-on), the third way a
 * card is grouped beside its area and its epic. A card still carries label NAMES
 * in `Issue.labels`; this is the board's metadata for a name — its optional
 * colour and whether it is still offered.
 */
export interface Label {
  id: string;
  name: string;
  /**
   * The label's colour as a hex, or null/absent for "no colour". A new label is
   * AUTO-COLOURED on creation (Vinnie, 5 Nov) from the identity palette — the same
   * set areas and epics draw from — so most labels carry a colour; null is the
   * opt-out for a label a board deliberately clears back to a plain neutral chip.
   */
  colour?: string | null;
  /**
   * WHEN IT WAS RETIRED; absent or null means it is still live. Retiring is what
   * "remove" does — the label stops being offered and keeps its colour on every
   * card already carrying the name, so counts still add up (as for areas).
   */
  retiredAt?: number | null;
}

/** Only the labels still on offer, by name. Retired ones stay readable elsewhere. */
export function liveLabels(labels: Label[]): Label[] {
  return labels.filter((l) => !l.retiredAt).sort((a, b) => a.name.localeCompare(b.name));
}

/** The colour a label name wears, or undefined for none — looked up in the live registry. */
export function labelColour(name: string, labels: Label[]): string | undefined {
  const hit = labels.find((l) => !l.retiredAt && l.name.toLowerCase() === name.trim().toLowerCase());
  return hit?.colour || undefined;
}

export interface Issue {
  id: string;
  ref: string; // display reference, e.g. "#157" or "B4"
  kind: IssueKind;
  /**
   * WHICH AREA, by id, or absent for the great majority that have none.
   *
   * "No area" is a real answer and not a gap to be filtered away: 244 of this
   * board's 299 cards have none and nobody is going back over them. Every count
   * by area has to show it as its own row with its own number, or the totals
   * stop adding up and a fifth of the work goes missing from the picture.
   */
  area?: string | null;
  /**
   * WHICH PHASE it runs in, by id, or absent for the many that name none — #377.
   *
   * Like `area`, "No phase" is a real answer, not a gap to filter away: existing
   * cards will be null for a long time because nobody is slotting old work into a
   * phase. Every count by phase must show "No phase" as its own row, or the
   * totals stop adding up.
   */
  phase?: string | null;
  title: string;
  summary?: string; // plain-English explanation for non-technical readers
  blocked?: string; // short reason this item cannot move; empty/absent = not blocked
  blockerType?: BlockerType | ''; // what kind of blocker; '' = not set, fall back to detection
  /**
   * WHICH thing or person it is waiting on — an issue ref, an email address, or
   * a few words naming the test, decision or outside party.
   *
   * The kind alone says almost nothing: "a person needs to do something" cannot
   * be acted on, and six items waiting on "a test" may be waiting on six
   * different tests. With the specific target, work waiting on the same thing
   * groups together and clearing it genuinely releases all of them.
   */
  blockerTarget?: string;
  /**
   * EVERY thing blocking this card — the list form of the scalar above (#456,
   * jsonb column `blocker_refs`, migration 0157). When present it is the source
   * of truth; when absent the app derives a one-entry list from the scalar
   * `blocked` / `blockerType` / `blockerTarget`, so a card blocked before this
   * shipped reads correctly with nothing written. Writing the list keeps the
   * scalar (its first entry) in step, so the block clock, portfolio health and
   * the per-person grade — all of which read the scalar — are unchanged.
   */
  blockerRefs?: BlockerEntry[];
  /**
   * WHEN THIS BECAME BLOCKED, and WHEN IT CAME UNBLOCKED — milliseconds.
   *
   * `blockedAt` is the start of the current block; `unblockedAt` the end of the
   * last one, so a cleared card carries both and the wait is their difference.
   * Blocking again re-stamps the start and clears the end, so the pair always
   * describes the latest block.
   *
   * WRITTEN BY THE DATABASE, not the browser — migration 0098, for the reason
   * `completedAt` is (0096): the import and the tracker push write `blocked` too.
   * "Blocked" here means what the app means by it (src/blocked.ts) — the word
   * "no" is not a blocker. Auto-unblock by a finished dependency is NOT recorded
   * here: that card's own row never changes, and the instant is the blocker's
   * `completedAt`. The app only ever reads these.
   */
  blockedAt?: number;
  unblockedAt?: number;
  /** Roughly how much work it is. Belongs to the board, so a push never resets it. */
  size?: IssueSize | '';
  /**
   * WHAT THE WORK IS JUDGED TO COST, on the scale the customer uses — CPFC use
   * 2 to 8. Nobody-has-said is not the same as zero.
   *
   * IT DOES NOT REPLACE `size`, by Vinnie's decision of 22 August 2026. Size is
   * three buckets for planning a fortnight; difficulty is the number a client
   * is shown on a change request. Two questions, two answers.
   *
   * NULL IS ALLOWED ALONGSIDE ABSENT, and they mean different things on the way
   * OUT even though they read the same on the way in. Absent says "this record
   * is not talking about difficulty"; null says "set it to nobody-has-said".
   * Only the second can empty a column, because a key whose value is undefined
   * does not survive being turned into a request — see the note on the save in
   * IssueModal, where taking a number back out silently did nothing.
   */
  difficulty?: number | null;
  /**
   * WHEN WORK ACTUALLY BEGAN — the first move into In Progress.
   *
   * Cycle time, the number a team actually feels, runs from starting a thing to
   * finishing it. Measuring from `createdAt` instead measures how long it sat in
   * the backlog — a real number about something else entirely — and quoting one
   * for the other is how a team gets told it is slow when it is queueing.
   *
   * SET ONCE AND NEVER REWRITTEN. A card pulled back out and started again has
   * not started twice.
   */
  startedAt?: number;
  /**
   * WHEN IT FINISHED — the moment it entered done or won't-do.
   *
   * The other half of cycle time, and the one the board could never say before:
   * "was it done, and when?" was only guessable from the last "moved to done"
   * line in Activity, which is absent for an imported-as-done card and lost if
   * the trail is ever trimmed. So throughput and aging were being read off
   * `updatedAt` — the last time anyone touched the card — which moves whenever
   * somebody ticks a check on work that finished a month ago.
   *
   * WRITTEN BY THE DATABASE, not the browser — migration 0096, the same reason
   * Activity (0030) and the started-at clamp (0033) live there: the import and
   * the tracker push write cards too. Cleared if the card is reopened, so it
   * always holds the LAST time it finished. The app only ever reads it.
   */
  completedAt?: number;
  /**
   * WHEN THIS CARD IS MEANT TO BE DONE BY — a target the person sets, not a
   * moment the database stamps. Unlike startedAt/completedAt above (system
   * instants in milliseconds), this is a plain `YYYY-MM-DD` DAY, the same shape
   * a phase's `targetEnd` and a step's `due` already use (see phases.ts), and
   * for the same reason: a due date is a day on a calendar, and storing it as an
   * instant would shift it across a timezone. It stays OUT of the ms-conversion
   * set in paths.ts. Migration 0106; column `due_date` of type `date`.
   *
   * NULL ALONGSIDE ABSENT, like `difficulty` above: absent means the record is
   * not talking about a due date; null clears the column. A key left undefined
   * would not survive being turned into a request, so the modal sends `|| null`.
   */
  dueDate?: string | null;
  /**
   * WHEN WORK ON THIS CARD IS MEANT TO BEGIN — the partner to `dueDate`, so a
   * card can carry a span (start → target) rather than a single deadline. It
   * DELIBERATELY reuses the name a phase's start already uses (`startsOn` →
   * column `starts_on`, see phases.ts and paths.ts): both are a plain
   * `YYYY-MM-DD` DAY a person sets and both are kept OUT of the ms-conversion set
   * in paths.ts so they never shift across a timezone. It is NOT called
   * `startDate`, because that field maps to `start_date`, which a board (#387)
   * owns as a `timestamptz` and IS converted — sharing it would timezone-shift a
   * card's day. Migration 0107; column `starts_on` of type `date`. NULL clears
   * it, absent means unspecified — the modal sends `|| null`, like the due date.
   */
  startsOn?: string | null;
  /**
   * HOW THE DATES ABOVE HAVE MOVED — migration 0125, all four written by the
   * database, never by the app. Each baseline is the day its date was FIRST set
   * (`YYYY-MM-DD`, forward-only, kept out of the ms set); slip is current minus
   * baseline. Each push count is how many times that date has moved LATER (a move
   * earlier is not slippage and is not counted). They drive the "originally 12
   * Sep · +14d · moved 3×" read on the card, the face mark after two pushes, the
   * "Most pushed" backlog sort and the Insights slippers panel. Absent on a card
   * that has never carried a date.
   */
  dueDateBaseline?: string | null;
  startsOnBaseline?: string | null;
  dueDatePushes?: number;
  startsOnPushes?: number;
  /**
   * HOW FAR ALONG THE WORK IS — a 0–100 percent a person SETS BY HAND (the
   * responsible doer, including a vendor, or a manager), not derived from status.
   * It is the number a vendor's PM reports, and it gives a card a real progress
   * reading between "In progress" and "Done". Read against the card's `startsOn`→
   * `dueDate` window and the pace held so far it drives the on-track / behind /
   * won't-make-it signal (progress-health.ts) on the card face, in the card and in
   * the portfolio roll-up. Migration 0199; column `progress_pct` of type
   * `smallint`. NULL clears it (nothing recorded — no bar shown); absent means the
   * same, so the modal sends `|| null`, like the dates above. The store clamps 0–100.
   */
  progressPct?: number | null;
  /**
   * ON HOLD — an explicit, deliberate pause on this one card (project-card-hold,
   * migration 0201). `heldAt` is the moment it was paused (ms, like the other date
   * stamps); absent/null means the card is running. `holdReason` is an optional note
   * that shows on the hold badge and rides into the audit trail.
   *
   * A held card is treated as paused everywhere it matters: the idle nudge skips it
   * (idle-nudge.ts), its schedule health reads a neutral "On hold" instead of
   * behind / at-risk, and it drops out of the portfolio slip and progress-at-risk
   * counts. It KEEPS its lane — hold is a flag, not a status. On resume the store
   * moves `startsOn` and `dueDate` out by the time held, and that shift cascades to
   * dependents. Distinct from the derived "Started · on hold" actuals label
   * (ACTUAL_PARKED_LABEL), which only describes a card sent back a lane; this is the
   * primary, intentional signal. Mirrors the board-level hold on ProjectMeta.
   */
  heldAt?: number | null;
  holdReason?: string;
  /**
   * WHICH SPRINT THIS IS IN. Absent or empty means the backlog.
   *
   * THE DEFAULT HAD TO BE "NO SPRINT" and it is worth saying why, because the
   * other reading is superficially friendlier. Every issue that exists predates
   * this field, so treating absent as "the sprint that is running" would sweep a
   * year of finished and abandoned work into whatever fortnight starts next —
   * and every chart drawn from the first sprint would be nonsense, in a way that
   * looks like a very productive fortnight rather than like a bug.
   */
  sprint?: string;
  /**
   * WHEN IT JOINED THE SPRINT IT IS IN NOW.
   *
   * This is the whole of committed-versus-added: work in the sprint before it
   * started was committed, work that arrived after was scope added mid-flight,
   * and the say-do ratio every retrospective asks about is the ratio of the two.
   * After the fact the two are indistinguishable, so it is recorded from the
   * first day rather than the day a sprint report gets built — the same reason
   * `startedAt` above went in before anything measured cycle time.
   */
  addedToSprintAt?: number;
  /**
   * The project's definition of done, ticked off for this issue.
   *
   * Keyed by the wording of each check, so an issue keeps a readable record of
   * what was actually agreed at the time — if the project later changes its
   * list, finished work still shows what it was held to.
   */
  checks?: Record<string, boolean>;
  /**
   * THE CARD'S OWN CHECKLIST — a list of tickable steps unique to THIS card.
   *
   * Distinct from `checks` above: `checks` ticks the BOARD's one shared Definition
   * of Done (the same list on every card); this is a list the card authors for
   * itself — different on every card — for the sub-steps that make up its work.
   * Ticking its items moves the card's effective progress % (progress-derive.ts):
   * done ÷ total, taking precedence over the board DoD. Order is array order
   * (these are sequenced steps, not a set). Each item's `id` is a real uuid
   * (crypto.randomUUID()), never an array index, so an item keeps its identity
   * through reorders and edits. Absent/empty means the card has no checklist and
   * the % falls through to the board DoD, exactly as before (migration 0211,
   * jsonb column `checklist`). Rides as JSON like `standsLog`/`blockerRefs`.
   */
  checklist?: ChecklistItem[];
  /**
   * FILES ATTACHED TO THIS CARD — the card's own attachments (dossier v3 §6,
   * migration 0223), replacing the dropped card-level RAID. A CardAttachment[]:
   * each entry is the metadata of one file kept in the private `documents` bucket
   * under a `board/card-<id>/name` key (src/documents.ts fileKey). The bytes live
   * in storage; this list is what the card shows. Absent/empty means no files.
   * Rides whole as JSON like `checklist`/`checks` — no paths.ts entry, covered by
   * the existing issue UPDATE policy.
   */
  attachments?: CardAttachment[];
  epic?: string; // theme this item belongs to, for grouping a long backlog
  stands?: string; // "where this stands" — current concrete state, heads the technical notes
  /**
   * THE DATED STATUS LOG — a human progress note, one entry per update.
   *
   * `stands` above is a single string: the state as it is right now. This is the
   * history of how it got there — who said what changed, and when — newest-first
   * when shown. It is deliberately NOT the automatic activity trail (`activity`):
   * that records every field edit the machine saw; this records the few the
   * person chose to narrate. The latest entry's text is mirrored back into
   * `stands`, so everything that reads `stands` today (the portfolio pack, the
   * card's notes) keeps showing the current state with no change.
   *
   * A status CHANGE appends an entry of its own (`auto: true`), so the record
   * stays complete whether the writer narrates the move or just drags the card.
   */
  standsLog?: StatusUpdate[];
  description: string;
  status: IssueStatus;
  priority: IssuePriority;
  /* HOW ESSENTIAL FOR THE RELEASE (MoSCoW) — set by a person, absent until then;
     distinct from `priority` (urgency). Null alongside absent, like `area` and
     `difficulty`: clearing it back to "not set" has to travel as a value. */
  moscow?: MoscowLevel | null;
  /* RUN/CHANGE override (#435). Absent/null means "follow the type" — the class is
     derived from the card's Means. A value is stored ONLY when a person has set it to
     something other than the derived class; picking the derived value again clears it,
     so a later type change still flows through untouched cards. See src/investment.ts. */
  investmentClass?: InvestmentClass | null;
  assignee: string;
  // the doer (RACI Responsible); absent = no doer named yet. `assignee` above is
  // the Accountable person — both are optional and may be the same person.
  responsible?: string;
  labels: string[];
  builds?: number[]; // build numbers that carried this item, newest first
  commits?: { sha: string; short: string; date: string; subject: string }[];
  /**
   * THE FILE THIS CARD ARRIVED ON, when it did not come from a person typing.
   *
   * Absent on everything raised by hand, which is every card that existed
   * before the spreadsheet import. It decides the one line the history writes
   * when the card is created — "imported this item from X" rather than "added
   * this item to the board", which was true but said nothing about how it got
   * there (Vinnie, step 5.11 of the walk, 20 August 2026) — and it is the only
   * way to answer "which of these did we bring over, and from what" once the
   * spreadsheet is gone.
   */
  importedFrom?: string;
  createdAt: number;
  updatedAt: number;
  comments: Comment[];
  activity?: Activity[];
}

/**
 * ONE ENTRY IN A CARD'S STATUS LOG (`Issue.standsLog`).
 *
 * The writer types only `text` — "what changed". Everything else is stamped:
 * `by` is their address, `at` the moment they posted. `status` is set only when
 * the update also moved the card, so the log line can read "Moved to In
 * progress. …" and the timeline shows the transition. `nextStep` is the one
 * optional extra — "what happens next" — shown as a chip under the entry.
 *
 * `auto` marks the line the app writes itself when a status changes without a
 * narration, so the record is never silent about a move. Auto lines are shown
 * like any other but raise no call-out — a status change already tells the
 * people it needs to.
 */
/**
 * ONE ITEM IN A CARD'S OWN CHECKLIST (`Issue.checklist`).
 *
 * `text` is the step's wording; `done` is whether it is ticked. `id` is a real
 * uuid (crypto.randomUUID()) minted once when the item is created and kept
 * through every reorder and edit — never the array index, so ticking item three
 * after a reorder ticks the right step. Position in the array IS the order.
 */
export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

/**
 * ONE FILE ATTACHED TO A CARD (`Issue.attachments`, migration 0223).
 *
 * `path` is the object key in the private `documents` bucket
 * (`board/card-<issueId>/name`); the download link is issued short-lived against
 * it (store.cardFileUrl), never stored. `by` is the uploader's address and `at`
 * the moment it went up, so the file row reads "who · when" like a document row.
 * `id` is a uuid minted on upload, the handle the remove action uses.
 */
export interface CardAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  path: string;
  by: string;
  at: number;
}

export interface StatusUpdate {
  id: string;
  at: number;
  by: string; // the author's address; '' for a line with no human author
  text: string;
  status?: IssueStatus; // set when this entry also changed the card's status
  nextStep?: string;
  auto?: boolean; // the app wrote this line for a bare status change
  /**
   * THIS LINE RECORDS A CASCADE DATE SHIFT — the card's dates moved because a card
   * it waits on had its finish pushed later (src/dependencies.ts cascade). Always
   * an `auto` line; this flag marks it so the database can tell the card's doer
   * their dates moved (migration 0203 notify_on_cascade_move) without parsing the
   * wording. A hand-typed update never carries it.
   */
  cascade?: boolean;
  /**
   * A QUIET NOTE — posted with the "Note" setting rather than "Update", so it
   * records what happened without telling the card's people. An Update (this
   * absent) notifies the people on this card; a Note raises no call-out of its own,
   * exactly like an `auto` line, though it reads as an ordinary human entry. An
   * @mention inside it still reaches the person named — that is an explicit ping,
   * separate from the broadcast this flag silences.
   */
  quiet?: boolean;
  /**
   * WHEN THIS UPDATE'S WORDS WERE LAST CHANGED, and by whom. Absent until the
   * author (or an admin) edits it. A status update is the running narration of
   * where things stand, so "awaiting feedback" becoming "feedback received" is a
   * genuine edit — the card shows a quiet "edited" mark against the entry. An
   * edit never re-notifies: the notify trigger fires only when the log GROWS (a
   * new entry), not when an existing one is rewritten (migration 0138).
   */
  editedAt?: number;
  editedBy?: string;
  /**
   * WITHDRAWN — the author (or an admin) took this note or update back. Soft,
   * exactly like a removed comment: the entry stays in the log, so an admin can
   * still review what was withdrawn and by whom, but everyone else is shown
   * nothing — it drops out of the feed, the update count and any re-derivation of
   * where the card stands. A status MOVE is never withdrawable, so these never
   * sit on a line that carries `status`. Kept rather than deleted so the words
   * are recoverable; the plain removal is still written to the audit trail.
   */
  removed?: boolean;
  removedAt?: number;
  removedBy?: string;
}

export interface Build {
  id: string; // "b102"
  build: number;
  date: string; // YYYY-MM-DD, or '' when the notes never recorded one
  dateInferred?: boolean; // date recovered from git/neighbours, not recorded at the time
  version: string;
  items: string[]; // issue refs carried, e.g. ["#198", "#163"]
  plain: string; // one plain-English sentence
  installed: string; // what it was installed on, comma-separated — only on boards that track devices
  /**
   * WHEN APPROVAL WAS ASKED FOR. Not when the work was asked for
   * (ProjectMeta.requestedOn) and not when anybody signed — three dates, three
   * questions. This is the one that says a row has been outstanding a fortnight.
   */
  signoffRequestedAt?: number | null;
  /** When it was cut — approval earns this. Absent means prepared. See 0050. */
  cutAt?: number | null;
  /**
   * WHEN IT ACTUALLY WENT LIVE, on a board that signs releases off.
   *
   * Absent means it has not gone live, and that is now a plain fact rather
   * than an inference. It used to mean "not live UNLESS nobody was asked to
   * sign", which could not tell a release nobody signed from one nobody has
   * signed yet — the exact difference "prepared" introduced. Migration 0050
   * wrote the answer down for every release that already existed, and a board
   * that signs nothing still has this set the moment a release is recorded.
   * See `releaseState` in src/releases.ts — the question is asked in one place so
   * two screens cannot answer it differently.
   */
  wentLiveAt?: number | null;
  /**
   * Which of `installed` a TOOL reported, rather than a person typing it.
   *
   * The list has two authors and they know different things. A tool knows what
   * it pushed at the moment it pushed; a person knows the Fold was done by cable
   * two days later and the S26 updated itself overnight — neither of which the
   * tool was there for. So the list keeps growing after the release goes out,
   * and both halves are real.
   *
   * Kept as a SUBSET of `installed` rather than a second list, because the
   * display is one list and the only question ever asked of this field is "did a
   * person say this, or did software?" — which is what to distrust when the list
   * disagrees with the world. See mergeDevices in src/releases.ts.
   */
  installedTold?: string;
  confidence: 'high' | 'low';
  /**
   * The sprint this was cut at the close of — see migration 0021.
   *
   * ABSENT IS THE ORDINARY CASE. A hot-fix, a release cut on a Tuesday, and a
   * board that does not work in sprints all have none, and none of them is a
   * lesser release. Recording it lets a finished sprint say what it shipped
   * without a release ever having to belong to one.
   */
  sprint?: string;
  /**
   * THAT THIS BUILD IS NO LONGER ONE ANYBODY SHOULD BE RUNNING — see migration
   * 0022. Vinnie, 15 August: *"a release can be marked as withdrawn or rolled
   * back."*
   *
   * TWO WORDS BECAUSE THEY ARE TWO EVENTS. `withdrawn` is a build taken out of
   * circulation with nothing put in its place — a store pulled the binary, a
   * certificate was wrong. `rolledback` is a build replaced by an earlier one,
   * which is a different fact for whoever is reading this in a hurry: something
   * is running out there, and `pulledBack` says what.
   *
   * ABSENT IS THE ORDINARY CASE and means the release stands.
   */
  pulled?: 'withdrawn' | 'rolledback';
  /** Why, in one line. Required to pull — see `pullProblem`. */
  pulledWhy?: string;
  pulledAt?: number;
  pulledBy?: string;
  /** The version put back in its place, when one was. Only for `rolledback`. */
  pulledBack?: string;
}

/** A person who signs work off: a name and the position they hold. Separate,
 *  because a position outlasts the person and because one real title reads
 *  "Marketing, Customer Experience and Shared Services Manager". */
export interface Signatory {
  name: string;
  position: string;
  /**
   * THEIR ACCOUNT HERE, IF THEY HAVE ONE.
   *
   * Optional, and absent is the ordinary case — a client's sponsor is not going
   * to be given a login, which is the whole reason a panel row is typed text
   * rather than a person. When it IS set, that account answers their own row
   * first-hand at any rank, and nobody records it for them.
   *
   * NOT PART OF WHO SOMEBODY IS. Two rows with one name and one position are
   * the same person however the address is spelled, so the roster still matches
   * on those two and this rides along.
   */
  email?: string;
  /**
   * WHICH STEP OF THE CHAIN THIS PERSON ANSWERS — #352.
   *
   * Vinnie, 4 September 2026, looking at the built screen: *"where is the
   * tester themselves?"* A step could be given nothing but a RANK, and a tester
   * is not a rank in this product and should not become one — so the step named
   * after the tester was the one step that could not ask them.
   *
   * ⚠️ IT LIVES ON THE PERSON RATHER THAN ON THE STEP, and that is the whole
   * reason it is here. A step holding its own list of people would be the
   * second list of signatories this product has, and #332's note above says
   * exactly what that costs: somebody edits the wrong one and cannot see why
   * the sheet is wrong. One list; each row says when it is asked.
   *
   * Absent on a board with no chain, where it means nothing — everybody on the
   * panel is asked at the one approval there is, exactly as before this existed.
   */
  step?: SignoffStep;
}

/**
 * ── WHO SIGNS, AS A RULE RATHER THAN A LIST OF NAMES — #332 ─────────────────
 *
 * The shape lives here beside `Signatory` because `types.ts` imports nothing:
 * the reading of it is `src/signoff-policy.ts`, which imports Signatory from
 * here, so declaring it there and importing it back would be a cycle.
 *
 * ⛔ THREE RANKS, NOT ALL FIVE. Viewer, reviewer and member read and do the
 * work; a rule saying "every member must sign" describes a board where nothing
 * is ever approved. Approval is the owner's, the manager's and the admin's. A
 * board wanting anybody else names them in `named`.
 */
export type SignoffRole = 'owner' | 'manager' | 'admin';

/**
 * ── WHICH STEP OF THE CHAIN A SIGNATURE IS — #346 ───────────────────────────
 *
 * Vinnie, 4 September 2026: the tester signs off, then the manager, then the
 * project owner, and only then does it go live. Three signatures, each gating
 * the next.
 *
 * ⚠️ `''` IS A REAL VALUE AND IT IS NOT "UNSET". It is a plain approval —
 * somebody approved this — which is what every signature collected before this
 * existed was, and what a board with no chain goes on collecting. Reading it as
 * "missing" would put every release signed since 0039 into a process nobody
 * asked them to follow.
 */
export type SignoffStep = '' | 'uat' | 'manager' | 'owner';

/** One step of the chain: what it is, and which ranks answer it. */
export interface SignoffStepRule {
  step: SignoffStep;
  /** Ranks asked at this step. Empty means the step is declared and asks nobody. */
  roles: SignoffRole[];
}

/**
 * ── WHO SIGNS, AS NAMED SLOTS RATHER THAN RANK BUCKETS — the sign-off redesign ─
 *
 * The rank chain asked everyone who HELD a rank, so a board admin — a permission,
 * not an approver — landed on the sheet unasked, and the list grew with the team
 * rather than with intent (Vinnie, 6 September 2026, on a sheet that reached 13).
 * Slots ask NAMED people only: the developer who ran it, the tester(s), the one
 * approving manager, and the owner. No rank ever adds anybody.
 *
 * ⚠️ PRESENCE OF `slots` IS THE SWITCH, and that is the whole of the back-compat.
 * A board that carries slots resolves who-signs the new way; a board that does
 * not keeps the rank chain it already had, untouched, until an admin sets slots
 * up — so no live board's sign-off changes under it silently. The people for the
 * UAT and Approver slots are the named signatories tagged to those steps (`uat`
 * and `manager`); the owner is the owner fields; the developer is resolved at the
 * moment a sign-off is raised, never stored as a rank.
 */
export interface SignoffSlots {
  developer: boolean;
  uat: boolean;
  approver: boolean;
  owner: boolean;
}

/**
 * ── ONE MEMBER OF A BOARD-DEFINED STAGE — configurable sign-offs R1 ───────────
 *
 * The rank chain (#346) and the slots redesign both answered "who signs" from a
 * FIXED vocabulary of four steps the board could only turn on or off. R1 lets a
 * board define its OWN ordered stages and say, per stage, who answers it — and a
 * stage's member is one of three things rather than always a rank:
 *
 *   role     — a rank read at the moment of asking, exactly as #332 does it: the
 *              project owner, the board's managers, workspace admins. `role` is
 *              the SignoffRole it resolves. This is why nobody has to maintain a
 *              name when staff change — the whole reason #332 chose a rule.
 *   person   — somebody with an account here, referenced by `personRef` (their
 *              address). Resolved against the roster at the moment of asking so
 *              their current name is used, then frozen onto the row like any other.
 *   external — somebody with no account and never will have one: a client's
 *              sponsor, an executive director. Typed outright — `name`,
 *              `position`, `email` — because there is nothing to reference. This
 *              is the `Signatory` shape that has existed since before #332.
 *
 * ⚠️ THE KIND DECIDES WHICH FIELDS MATTER, and the reader validates on it: a
 * `role` member with no `role`, or an `external` with no name/position, is a
 * broken member and is dropped rather than guessed at. Who-signs is a crown
 * jewel; a malformed member must never silently become somebody it did not name.
 */
export interface SignoffStageMember {
  kind: 'role' | 'person' | 'external';
  /** Set when kind is 'role': the rank this member resolves to. */
  role?: SignoffRole;
  /** Set when kind is 'person': the account (address) this member references. */
  personRef?: string;
  /** Set when kind is 'external': a typed person with no account here. */
  name?: string;
  position?: string;
  email?: string;
}

/**
 * HOW MANY OF A STAGE'S MEMBERS MUST ANSWER FOR THE STAGE TO PASS.
 *
 *   'all'          — every member signs. The chain and slots models' rule today,
 *                    and the safe default: a stage that asks three people and
 *                    counts as done on one is how a sign-off stops being evidence.
 *   'anyOne'       — one of them is enough (a pool of interchangeable approvers).
 *   { quorum: n }  — at least n of them. The general case the other two are ends
 *                    of; a board that wants "two of the three directors".
 */
export type SignoffStageMode = 'all' | 'anyOne' | { quorum: number };

/**
 * ── A BOARD-DEFINED STAGE — configurable sign-offs R1 ─────────────────────────
 *
 * One ordered stage of a board's sign-off: an id, a label the board chose, where
 * it sits (`order`), the people it asks, and how many of them must answer. Unlike
 * `SignoffStep` this is not a fixed vocabulary — a board writes its own.
 *
 * ⚠️ `order` IS THE ORDER IT IS ASKED IN, but it is not what gets frozen onto a
 * row. The store turns the stages' `order` into a DENSE RANK (0,1,2… with no gaps
 * or ties) and freezes THAT as `signoffs.stage_seq` (migration 0129), so a board
 * editing its stages after a sheet is raised cannot reorder a sheet already out.
 * `order` is only the sort key that dense rank is computed from.
 */
export interface SignoffStage {
  id: string;
  label: string;
  order: number;
  members: SignoffStageMember[];
  mode: SignoffStageMode;
  /** An optional stage is not required for the sheet to be approved. A policy of
   *  nothing BUT optional stages asks nobody and is refused, like an empty rule. */
  optional?: boolean;
}

export interface SignoffPolicy {
  /**
   * THE CHAIN, IN ORDER. One step is the ordinary case — a board that just
   * wants an approval has a single unnamed step, which is exactly the rule
   * #332 stored and is why the old shape reads forward without a rewrite.
   */
  steps: SignoffStepRule[];
  /** People named outright — with an account here, or without one. */
  named: Signatory[];
  /** Raise one automatically the moment a sprint is finished. */
  onSprintFinish: boolean;
  /** Set once a board is on the named-slot model; absent keeps the rank chain. */
  slots?: SignoffSlots;
  /**
   * SET ONCE A BOARD IS ON THE STAGE MODEL — configurable sign-offs R1.
   *
   * Presence of `stages` (a non-empty array) is the back-compat switch, exactly
   * as `slots` presence is: a board carrying stages resolves who-signs and the
   * order the stage way; a board with none keeps whichever of the older two
   * models it already had, untouched. No live board's sign-off changes under it.
   */
  stages?: SignoffStage[];
}

/**
 * THE RULE AS IT MAY BE FOUND ON A BOARD, which is either shape.
 *
 * Boards carry the rule #332 wrote — `{roles, onSprintFinish}` — and nothing
 * rewrites them on the way past, because a migration that rewrites a rule is a
 * migration that can rewrite it wrongly. `readPolicy` in signoff-policy.ts is
 * the one place that reads either and hands back the chain.
 */
export interface StoredSignoffPolicy {
  steps?: SignoffStepRule[];
  /** #332's shape: the ranks asked all at once, with no step and no order. */
  roles?: SignoffRole[];
  onSprintFinish?: boolean;
  /** Present once a board is switched to the named-slot model — see SignoffSlots. */
  slots?: SignoffSlots;
  /** Present once a board is switched to the stage model — see SignoffStage. Its
   *  presence (a non-empty array) is the switch, and `readPolicy` tests for it
   *  first, ahead of steps/roles/slots. Configurable sign-offs R1. */
  stages?: SignoffStage[];
}

/**
 * ── DEFAULT + PER-TYPE OVERRIDES — configurable sign-offs R1 ──────────────────
 *
 * A board used to store ONE `StoredSignoffPolicy` applied to everything it signs.
 * Vinnie, 30 September 2026: "Default + override any type." So a board can carry a
 * board DEFAULT plus optional per-type overrides — a release signed by the IT
 * managers only, say, while sprints and documents keep the default.
 *
 * ⚠️ THE PRESENCE OF `default` IS THE VERSION SWITCH, exactly as `stages` presence
 * switches the who-signs model. A board that has never made an override still
 * stores a plain flat `StoredSignoffPolicy` and is read forward as
 * `{ default: <that policy>, overrides: {} }` — nothing rewrites stored data. The
 * config shape is written only once a board actually overrides a type, so no live
 * board's `signoff_policy` changes shape under this.
 *
 * A `typeKey` is `'sprint'`, `'release'`, or a signable `DocumentKind` (charter,
 * requirements, change, feature, improvement, deliverables, test — never a
 * received kind). `policyForType` in signoff-policy.ts resolves a subject to its
 * override if one exists, else the default.
 */
export interface StoredSignoffConfig {
  default: StoredSignoffPolicy;
  overrides: Record<string, StoredSignoffPolicy>;
}

/**
 * ── PAYMENT-APPROVAL CHAIN — the executive-approver tier (4 Oct 2026) ─────────
 *
 * Who AUTHORISES a supplier payment is a CONFIGURABLE ORDERED CHAIN, the same
 * primitive as the sign-off chain (SignoffStage) and deliberately so: one dialect,
 * not two. It reuses `SignoffStageMember` (a role shortcut / a named person / an
 * outside person) and `SignoffStageMode` (all / any one / a quorum), and adds ONE
 * new thing the sign-off side has no use for — a value BAND that gates a stage by
 * the payment's size.
 *
 * ⚠️ NOT THE SAME RECORD as sign-offs. Sign-offs authorise a DELIVERABLE
 * (a sprint, a release, a document); this authorises MONEY. They share the shape
 * and the resolver, never the stored object — so a release policy and a payment
 * policy never fight over one config. Vinnie approved this model and its three
 * surfaces on 4 Oct 2026 (dev-preview/payment-approval/).
 *
 * WHO MAY BE A SIGNER. A role member resolves fresh like the sign-off chain. A
 * named-person member is only offerable when that person holds the workspace
 * EXECUTIVE-APPROVER designation (the People badge) — so money authority is granted
 * deliberately, never inherited from being a workspace admin.
 */

/**
 * A VALUE GATE on one payment stage — the one primitive the sign-off chain lacks.
 *
 * Absent on a stage → the stage applies to EVERY payment. Present → the stage
 * applies only when the line's amount, converted to the workspace BASE currency,
 * is at or above `minAmountBase`. A band ADDS a stage above the threshold; it never
 * removes the stages below it (the executive signs IN ADDITION to the manager).
 * The base-equivalent is frozen when the chain is first raised, so a moving FX rate
 * cannot re-gate a part-signed line (see the store's raise path).
 */
export interface PaymentBand {
  /** The threshold in the workspace base (reporting) currency, whole base units. */
  minAmountBase: number;
}

/**
 * ONE ORDERED STAGE of a payment-approval chain. Mirrors `SignoffStage` field for
 * field — id, board-chosen label, `order` (turned into a frozen dense rank on the
 * line exactly as sign-offs freeze `stage_seq`), its members, and how many must
 * sign — plus the optional value `band`.
 */
export interface PaymentStage {
  id: string;
  label: string;
  order: number;
  members: SignoffStageMember[];
  mode: SignoffStageMode;
  /** An optional stage is not required for the chain to complete. A chain of
   *  nothing but optional stages authorises on no signatures and is refused, the
   *  same guard the sign-off stage model applies. */
  optional?: boolean;
  /** The value gate. Absent = applies to every payment. */
  band?: PaymentBand;
}

/** A payment-approval chain: the ordered stages a payment is authorised through. */
export interface PaymentApprovalPolicy {
  stages: PaymentStage[];
}

/**
 * STORED: a workspace DEFAULT chain plus optional per-project overrides, mirroring
 * `StoredSignoffConfig`. A project reads its override if it set one, else the
 * default. Absent default (nothing set) is the FIRST-RUN state — approval is
 * BLOCKED (never a silent "any admin"), surfaced as the "Set a payment approver"
 * screen. `overrides` is keyed by board/project id.
 */
export interface StoredPaymentApproval {
  default?: PaymentApprovalPolicy;
  overrides?: Record<string, PaymentApprovalPolicy>;
}

/**
 * ONE SIGNATURE ON ONE RELEASE.
 *
 * The name and position are COPIED from the panel when approval is requested,
 * not referenced: the panel is edited and a signed record is not, so "Chief
 * Information Officer" on release 12 stays whoever it was that month.
 */
/**
 * ── A REQUEST THAT AN ADMIN ADD SOMEBODY TO A BOARD — #359 ───────────────────
 *
 * Only admins invite (the ICT manager's decision, 4 September 2026), so a
 * developer who runs a board and needs a colleague — or an assistant — added
 * files one of these instead. An admin sees it and acts on it; the developer
 * never gains the power to add anyone, only to ask.
 *
 * Deliberately not a Signatory or a Person: it is a small, disposable record of
 * an ask, and it holds no credential ever. See migration 0078.
 */
export type BoardRequestKind = 'person' | 'assistant';
export type BoardRequestStatus = 'pending' | 'approved' | 'declined' | 'fulfilled';

export interface BoardRequest {
  id: string;
  /** The board they want somebody added to. */
  boardId: string;
  /** A colleague to invite, or an assistant to stand up. They are added and
     answered differently, so the kind is not cosmetic. */
  kind: BoardRequestKind;
  /** Who to add. For an assistant, the address it will be identified by — it
     need not be a real inbox. */
  email: string;
  displayName: string;
  /** The board role to grant. Capped at member: manager is heavier and stays
     an admin's to hand out directly. */
  role: 'viewer' | 'reviewer' | 'member';
  note: string;
  /** The person who asked. Their own address, fixed by the insert policy. */
  requestedBy: string;
  status: BoardRequestStatus;
  createdAt: number;
  /** The admin who approved or declined it, and when. Empty until they do. */
  decidedBy?: string;
  decidedAt?: number | null;
  /** The admin's short reason when they decline, shown to the asker so they
     know whether to re-file. Empty otherwise — G6, #359. */
  declineReason?: string;
}

/**
 * WHO COVERS A SIGNER WHILE THEY ARE AWAY — configurable sign-offs R1 (0130).
 *
 * When a person who is asked to sign work off goes on leave, they (or an admin on
 * their behalf) nominate a teammate to sign for them. The cover spans EVERY board
 * the covered person is on — it is not tied to one board — so there is no board
 * here. A person has at most one standing cover; turning it off keeps the row
 * (active=false) so their last nominee is remembered.
 */
export interface SignoffCover {
  id: string;
  /** The covered person, by their sign-in address (lowercased). Their pending and
     future sign-offs are the ones the nominee may sign. */
  delegator: string;
  /** The nominee who may sign on the delegator's behalf (lowercased address). */
  delegate: string;
  /** When the cover ends. Null keeps it on until it is turned off. */
  until?: number | null;
  /** The on/off the "When I'm away" toggle writes. */
  active: boolean;
  /** True when an admin arranged the cover for someone other than themselves.
     Stamped by the database from who the delegator is, never sent by the app. */
  setByAdmin: boolean;
  /** Why an admin arranged it — required when setByAdmin, so the trail always
     says why somebody was given cover. Empty for a self-set cover. */
  reason: string;
  /** Who arranged it (lowercased address), stamped by the database. */
  setBy: string;
  createdAt: number;
}

export interface Signoff {
  id: string;
  /**
   * WHAT THIS SIGNATURE IS AGAINST, and exactly one of the two is set — the
   * database refuses both and neither. A release carries `build`; a document
   * carries `documentId`. See migration 0045 for why they share one table.
   */
  build?: number;
  documentId?: string;
  /**
   * THE FINISHED FORTNIGHT THIS IS AGAINST — #332.
   *
   * The third of the three, and the reason the other two stopped being the
   * whole list: a team with no releases and no documents still finishes work
   * and still needs a record of who approved it. Migration 0075.
   */
  sprintId?: string;
  /**
   * WHICH STEP OF THE CHAIN THIS SIGNATURE IS — #346.
   *
   * Recorded on the row rather than worked out from `seq`, because a finished
   * sheet looks identical whether the steps were asked in order or all at once
   * — so the order has to be written down at the time or it cannot be shown
   * afterwards. Absent or `''` is a plain approval, which is every row
   * collected before this existed. See migration 0076.
   */
  step?: SignoffStep;
  /**
   * THE FROZEN STAGE ORDER — configurable sign-offs R1, migration 0129.
   *
   * Set only on rows raised against a board on the STAGE model: the dense rank
   * (0,1,2… no gaps, no ties) of the stage this row belongs to, frozen from the
   * board's stage definition at the moment the sign-off was raised. NULL/absent
   * on every rank-chain and slots row, and on every row collected before stages
   * existed — those are ordered by `signoff_step_order(step)` instead.
   *
   * The board's stage definition (`meta.signoffPolicy`) is jsonb and can be
   * edited after a sheet is out, so the order a row was asked in must be written
   * ONTO the row or it cannot be shown — or enforced — afterwards. The order
   * gate (`openStep`/`whyLocked` and the 0129 database trigger) reads
   * `stageSeq ?? stepOrder(step)`, so a later edit to the board's stages never
   * reshuffles a sheet already in flight. See src/signoff-policy.ts.
   */
  stageSeq?: number | null;
  /**
   * THE STAGE'S ANSWER RULE, FROZEN ONTO THE ROW — configurable sign-offs R1.
   *
   * How many of this stage's members must approve for the stage to clear: absent
   * (or 'all') means everyone, 'anyOne' means one is enough, `{quorum:N}` means at
   * least N. Frozen at raise like `stageSeq`, for the same reason — the board's
   * stage definition can be edited after a sheet is out, so the rule in force when
   * it was raised must ride on the row. Only set on stage rows whose rule is NOT
   * 'all' (the default); the database trigger `settle_stage_on_quorum` (0133) reads
   * it and withdraws a stage's still-unsigned rows once the rule is met, so the
   * existing approval logic — which already ignores withdrawn rows — reads the
   * stage as done. See src/signoff-policy.ts.
   */
  stageMode?: SignoffStageMode | null;
  /** Where it sits on the sheet. Order is seniority, so it is recorded. */
  seq: number;
  name: string;
  position: string;
  /**
   * THE ACCOUNT THIS ROW BELONGS TO, or '' when the signatory has none.
   *
   * Copied from the panel when the request is frozen, and fixed from then on —
   * a trigger in 0055 refuses to let it change, because a manager who may
   * update any unsigned row could otherwise rewrite a row to be theirs and sign
   * it first-hand, leaving the named person off a sheet that still looked
   * complete.
   *
   * When it is set, that person answers this row THEMSELVES at any rank, and
   * `onBehalfBy` stays empty. Being named is the permission.
   */
  signatoryEmail?: string;
  /** Null until signed. The only thing that is ever filled in afterwards. */
  signedAt?: number | null;
  /**
   * WHAT THEY ACTUALLY SAID, which is not the same as whether they answered.
   *
   * A real sign-off sheet has both — CPFC's carries Signature, Date, Approved
   * and Rejected as four separate columns — because signing is the act and
   * agreeing is the decision. Without this a refusal was indistinguishable from
   * a signature that had not arrived, so nobody chasing it could tell "on
   * leave" from "said no".
   *
   * Absent means approved, which is what every row written before this meant.
   */
  answer?: 'approved' | 'rejected';
  /** Why they said no. Required by the database on a rejection — a no with no
   *  reason sends the reader off to ask, which is the work the sheet saves. */
  why?: string;
  /** Set only when an admin signed for somebody who could not. Never blank on
   *  a row signed that way — the database refuses it — so a row that carries
   *  this is one that must be shown as what it is. */
  onBehalfBy?: string;
  onBehalfReason?: string;
  /**
   * A LINK OF THEIR OWN — #216, migration 0056.
   *
   * The people who sign a scope of works are a client's sponsor, an executive
   * director, a department head. None of them will be given an account, so
   * their answer used to reach the sheet second-hand. A link makes it theirs.
   *
   * ONLY THE HASH IS EVER STORED. The token is made in the browser, shown once
   * and never written down, so a copy of this table is not a set of working
   * links — which also means a mislaid link is replaced by issuing a new one
   * rather than looked up.
   */
  linkTokenHash?: string;
  linkIssuedAt?: number | null;
  linkExpiresAt?: number | null;
  /** When they first opened the document, which is what makes an approval mean
   *  they read it. Stamped once, by the function behind the page. */
  openedAt?: number | null;
  /** 'app' for somebody signed in, 'link' for the page an approver opens. */
  answeredVia?: 'app' | 'link';
  /** The fingerprint of the file the request was made against. A file replaced
   *  afterwards no longer matches, and the page refuses to be signed. */
  askedAgainst?: string;
  /**
   * SET WHEN THE REQUEST THIS ROW BELONGED TO WAS TAKEN BACK — 0058.
   *
   * The row leaves the panel the moment it is stamped, which is what
   * withdrawing means. Its link keeps working until its own expiry, and only so
   * that the page can say the request was withdrawn — a person sent a link that
   * morning should not be told it is not recognised.
   *
   * The store filters these out, so no screen in the app ever sees one.
   */
  withdrawnAt?: number | null;
  /**
   * A REFUSAL THAT WAS DISMISSED ON PURPOSE — Vinnie, 11 Sep 2026, migration 0094.
   *
   * Set together with `withdrawnAt` when a rejected request is cleared off the
   * open panel. Because it is also withdrawn, every active question already
   * excludes it; this marker is only so the Sign-offs screen can list it under
   * settled with its reason rather than the refusal vanishing. Null on every
   * other row. The store keeps these (unlike plain withdrawn rows) as
   * `dismissedSignoffs`, for that one settled section.
   */
  dismissedAt?: number | null;
  /** Who dismissed the refusal, for the settled line. */
  dismissedBy?: string;
}

/**
 * A DOCUMENT THE PROJECT IS DELIVERED AGAINST — see #193 and migration 0044.
 *
 * Three kinds, one shape: a business requirements document carrying the scope
 * of works, a change request when that scope moves, and a test script after a
 * sprint.
 */
/*
 * ORDERED AS THE WORK ARRIVES, not alphabetically: the requirements come first,
 * then the three ways more work is asked for after they were agreed, and the
 * test script is written against all of it. The screen offers them in this
 * order, so the order is the type's rather than the component's.
 */
export type DocumentKind =
  | 'requirements'
  | 'change'
  | 'feature'
  | 'improvement'
  | 'test'
  /* A sprint's completion sign-off — what was delivered, signed once the sprint
     is finished. Its sibling at the other end is the 'change' request that
     authorises the work at planning. See migration 0079. */
  | 'deliverables'
  /* A progress report — where the project stands this period, uploaded (or
     exported from the Portfolio pack) then routed for sign-off and filed like
     any other produced document. See migration 0156. */
  | 'statusreport'
  /* The after-action report — the retrospective a project writes when it closes:
     what it set out to do, what it delivered, what moved and why, and what to
     sustain or improve. Assembled from the board (see aar.ts) and filed like any
     other produced document. 3.19 Learning & Governance spine; migration 0174. */
  | 'aar'
  /* The founding document of a project — authored in-app in the rich-text editor
     or uploaded, then signed like any other. See migration 0102. */
  | 'charter'
  /*
   * RECEIVED / REFERENCE DOCUMENTS — handed to the project rather than produced
   * by it, kept for the record and referred back to, never sent round to be
   * signed here (migration 0111). A scope of work an external party wrote; the
   * project plan or schedule; an acceptance the customer returned; and plain
   * attachments — a topology diagram, a bill of materials. `isReceived` in
   * documents.ts is the single test for "is this one of these", and a received
   * kind is offered no signing controls.
   */
  | 'sow'
  | 'plan'
  | 'acceptance'
  | 'attachment';

/**
 * draft      being written, and the only state in which it can be changed
 * awaiting   sent for signature; the signatories are copied and frozen
 * signed     everybody named has signed — the database refuses this otherwise
 * superseded replaced by a later version, and kept exactly as it was signed
 */
export type DocumentState = 'draft' | 'awaiting' | 'signed' | 'superseded';

/**
 * THE PARTS OF A GENERATED DOCUMENT A PERSON WRITES — #195, migration 0062.
 *
 * Everything else a change request says is read from the board every time it is
 * opened, which is what makes a wrong item line fixable by correcting the card.
 * These are the parts nobody could infer: the narrative sections, keyed by the
 * template's own keys, and the assumptions table.
 *
 * ABSENT ON AN UPLOADED DOCUMENT, which is most of them. A scope of works is
 * written in Word and the file IS the document — there is nothing for this to
 * hold.
 */
export interface DocumentWriting {
  narratives: Record<string, string>;
  assumptions: { text: string; decision: string }[];
}

/**
 * THE AFTER-ACTION REPORT'S CONTENT — 3.19 Learning & Governance spine A1,
 * migration 0174.
 *
 * Unlike a change request (plain narrative strings in `written`), the AAR is RICH
 * TEXT: a closing PM formats the retrospective with headings, bold and lists in
 * the same editor the charter uses, so its four sections are stored as ProseMirror
 * RichDoc on the `aar` jsonb column, not as strings. The first three are composed
 * from the board (see aar.ts) and then edited; `sustain` is the person's own —
 * each line carries its own kind, and saving a line to the Lessons register stamps
 * `savedLessonId` so the editor shows it as already filed.
 */
export interface AarSustainLine {
  id: string;
  text: import('./rich-text').RichDoc;
  kind: import('./lessons').AarLineKind;
  /** The lesson this line was saved to, once it has been — drives the done-state. */
  savedLessonId?: string;
}
/** The variance FACTS, snapshotted at compose time so the frozen report (screen
 *  and PDF) can anchor a "why" prompt to the exact figure it already computed — the
 *  AAR is a point-in-time record, so it carries its own numbers rather than
 *  re-reading a board that may have moved on. A field is absent when its inputs are. */
export interface AarVariance {
  /** Go-live slip in days (actual − planned); positive = later than planned. */
  scheduleDays?: number;
  plannedGoLive?: number;
  actualGoLive?: number;
  /** Cost delta (forecast − baseline); positive = over budget. */
  costDelta?: number;
  budgetBaseline?: number;
  forecast?: number;
}
/** Where a promised benefit stands at close — realised, partly realised, or not
 *  yet (benefits often land after the project closes, so "not yet" is the honest
 *  default). Reuses the one status-badge shape: realised=g, partial=a, not-yet=n. */
export type AarBenefitStatus = 'realised' | 'partial' | 'notyet';
/** One expected benefit and where it stands — the closure question PMBOK/PRINCE2
 *  make mandatory: did the value the project was for actually land, and who tracks
 *  it after we close? Decision-tier: the headline, not a finance-grade ledger. */
export interface AarBenefitLine {
  id: string;
  /** The benefit, as rich text; seeded from the charter's success criteria. */
  statement: import('./rich-text').RichDoc;
  status: AarBenefitStatus;
  /** Who monitors it after close — a name or role. */
  monitor?: string;
}
export interface AarContent {
  supposed: import('./rich-text').RichDoc;
  actual: import('./rich-text').RichDoc;
  why: import('./rich-text').RichDoc;
  sustain: AarSustainLine[];
  /** The expected benefits and where each stands — seeded from the charter. */
  benefits?: AarBenefitLine[];
  /** The computed variance figures (snapshot), for the anchored "why" prompts. */
  variance?: AarVariance;
  /** Why the schedule ended where it did — rich text, shown only on an adverse slip. */
  scheduleWhy?: import('./rich-text').RichDoc;
  /** Why the cost ended where it did — rich text, shown only when over budget. */
  costWhy?: import('./rich-text').RichDoc;
}

export interface Doc {
  id: string;
  kind: DocumentKind;
  title: string;
  body: string;
  state: DocumentState;
  /** Only on a generated document — see DocumentWriting. */
  written?: DocumentWriting | null;
  /**
   * A CHARTER AUTHORED IN THE APP — its rich-text content as a ProseMirror
   * document (migration 0102). Present on a charter written in the editor;
   * absent on a charter uploaded as a file (there the file is the document) and
   * on every other kind. Stored as jsonb; frozen once the document leaves draft.
   */
  charter?: import('./rich-text').RichDoc | null;
  /**
   * THE AFTER-ACTION REPORT'S RICH-TEXT SECTIONS — only on an `aar` document
   * (migration 0174). Composed from the board at creation, then edited. Stored as
   * jsonb on the `aar` column; frozen once the document leaves draft. See
   * AarContent and aar.ts.
   */
  aar?: AarContent | null;
  /**
   * THE CARDS IT COVERED, FROZEN AT THE MOMENT THE PDF WAS PRODUCED — #195.
   *
   * A change request used to read its item lines live from the board on every
   * render, so a card corrected after approval silently changed what the signed
   * document showed. This is the snapshot taken when the PDF is produced, so the
   * on-screen document is the one that was signed; 0088 makes it immutable once
   * the document leaves draft, the same as the file it matches. Absent on an
   * uploaded document and on anything written before the snapshot existed, both
   * of which fall back to reading the board live.
   */
  scope?: ChangeRequest | null;
  /**
   * THE REQUIREMENTS DOCUMENT, FROZEN AT THE MOMENT ITS PDF WAS PRODUCED —
   * documents programme, increment 2b (migration 0114).
   *
   * Requirements is generated from the backlog (the requirement lines) AND the
   * project's About (purpose, scope, stakeholders, assumptions, constraints), both
   * read live on every render so a wrong line is fixed by correcting the card. The
   * snapshot taken when the PDF is produced freezes the whole composed document, so
   * the on-screen document is the one that was signed; the freeze guard makes it
   * immutable once the document leaves draft, the same as `scope` and the file it
   * matches. Absent on an uploaded document and on anything produced before this
   * existed, both of which fall back to composing it live.
   */
  requirements?: import('./requirements').RequirementsDoc | null;
  /**
   * THE GROUP THIS IS SIGNED OFF FOR, and absent means the whole project.
   *
   * Vinnie, 21 August: approval is given for a feature-set, not a card —
   * "I don't think any developer only gets approval to work on one issue or
   * one card, it would be a waste of time". So a document covers a sprint or
   * all of it, and there is deliberately no field naming a card.
   *
   * NAMED `sprint` RATHER THAN `sprintId` TO MATCH AN ISSUE, and not for
   * tidiness. The seam maps app field names to columns and builds the way back
   * by reversing that map, so two field names on one column means one of them
   * silently never returns. `sprint` was already mapped to `sprint_id` for
   * issues; a second name here would have read back as an issue's and left
   * every document with no sprint at all.
   */
  sprint?: string;
  /** Which reading this is, and the one it replaced. A version is a new row. */
  version: number;
  supersedes?: string;
  createdAt: number;
  updatedAt?: number;

  /*
   * THE FILE, WHICH IS THE DOCUMENT — #200.
   *
   * Vinnie, 21 August: nobody types a scope of works into a text box, and the
   * person asked to sign has to be able to read it. So these describe the
   * object in the `documents` bucket; the object itself is the only copy of
   * what it says. Absent on a draft nobody has uploaded to yet, and the
   * database refuses to send such a draft for signature.
   */
  fileName?: string;
  fileSize?: number;
  /** The MIME type. A PDF is read on the page; a Word file is downloaded. */
  fileType?: string;
  /** The object key: board_id/document_id/filename. */
  filePath?: string;
  uploadedAt?: number;
  /**
   * WHAT THE FILE ACTUALLY IS — sha256 of its bytes, taken in the browser as it
   * is uploaded (#216, migration 0056).
   *
   * A signature is against a document, not against a title. This is what a
   * signing link is issued against, so a file replaced afterwards no longer
   * matches and the page refuses to be signed rather than collecting an
   * approval for something nobody read.
   */
  fileHash?: string;
  /**
   * SHARED WITH VENDORS — whether a board's external supplier may read this
   * document (Feature B). Off by default: nothing reaches a vendor until someone
   * on the board turns it on, and the database enforces it (documents_read, 0196)
   * so a hidden row is never merely hidden. Budget- and payment-touching documents
   * are never offered the toggle — they stay internal whatever this says.
   */
  visibleToVendors?: boolean;
}

/**
 * THE AS-DELIVERED RECORD frozen at the go-live -> in-service handoff — #435
 * surface 2. The single most meaningful moment in a project's life, captured the
 * instant it is marked live, so the portfolio and the steering pack can show what
 * actually shipped against what was planned, and a later re-plan or rename cannot
 * rewrite it. Mirrors how AcceptanceEvidence freezes a phase acceptance. Rides
 * inside the `delivery_snapshot` jsonb on `boards` — its fields never become
 * columns, so the dates inside it (ms, the app's unit) are NOT in paths TIMES;
 * only the `actual_go_live_at` COLUMN is converted there.
 */
export interface DeliverySnapshot {
  /** The planned go-live at handoff time (ms), frozen from the project's `goLive`.
   *  Absent if the project never set a planned date. */
  plannedGoLive?: number;
  /** The actual go-live recorded at the handoff (ms) — the same value written to
   *  the `actual_go_live_at` column, kept here too so the snapshot is self-contained. */
  actualGoLive: number;
  /** Days the actual go-live ran past the plan (negative = early, 0 = on plan,
   *  absent = no planned date to compare) — the schedule variance the pack shows. */
  scheduleVarianceDays?: number;
  /** Delivery cards that shipped (status done) at handoff time. */
  scopeShipped: number;
  /** Delivery cards cut or still open and carried into service at handoff time. */
  scopeDeferred: number;
  /** Total delivery cards counted (shipped + deferred) at handoff time. */
  scopeTotal: number;
  /** The budget total at go-live (the app's money convention, via fmtMoney).
   *  Absent if the project carried no budget. */
  budgetAtGoLive?: number;
  /** True when the handoff was confirmed while a delivery phase was still open —
   *  the snapshot is then PROVISIONAL (the honest "not ready yet" variant). */
  provisional?: boolean;
  /** When the snapshot was taken (ms) — the handoff moment; equals actualGoLive
   *  only when the team marked it live as it happened. */
  at: number;
}

export interface ProjectMeta {
  name: string;
  key: string;
  nextNumber: number;
  /**
   * HOW THIS BOARD WRITES A CARD'S NUMBER. Absent means '#' and no padding,
   * which is how every board written here begins.
   *
   * It exists because of a board that was NOT written here. TRINRE arrived with
   * a spreadsheet numbered LH001 to LH076 and with signed requirements
   * documents pointing at those numbers; renumbering them to #1 to #78 on the
   * way in would have made every approved document wrong about what it
   * approved. Vinnie's decision, 19 August 2026: the numbering comes with them
   * and the board carries on from where their sheet left off.
   *
   * `refWidth` is the zero padding, so LH077 stays LH077 and does not become
   * LH77 the first time FlightDeck numbers one itself.
   */
  refPrefix?: string;
  refWidth?: number;
  repoUrl?: string; // e.g. https://github.com/owner/repo — makes commits clickable
  /**
   * THE PERSON WHO SIGNS THIS PROJECT'S WORK OFF — not `boards.owner`, which is
   * about permission and is on no screen. A name, not an account: the approver
   * need never open FlightDeck, so a sign-off is recorded as made on their
   * behalf by whoever pressed it. Empty means nobody has been named.
   *
   * TWO FIELDS, because a position outlasts the person holding it and because
   * one real title is "Marketing, Customer Experience and Shared Services
   * Manager" — no separator survives contact with that.
   */
  /**
   * THE CUSTOMER'S OWN LOGO, ON THE DOCUMENTS THAT LEAVE THE BUILDING.
   *
   * Vinnie, 23 August 2026: *"we should be able to upload a company logo so
   * that it becomes a branded form like everything else is supposed to be."* A
   * change request is signed by a client and kept in their file; it is their
   * document, not FlightDeck's, and an unbranded one reads as a printout.
   *
   * A STORAGE KEY, NOT THE IMAGE. Kept in the documents bucket beside the files
   * it appears on, so it is covered by the same rules about who may see a
   * board's things, and so a board's settings row does not carry a picture.
   */
  logoPath?: string;
  /** png or jpg — pdf-lib embeds the two differently and cannot guess. */
  logoType?: string;
  projectOwnerName?: string;
  projectOwnerPosition?: string;
  /**
   * THE OWNER'S ACCOUNT HERE, IF THEY HAVE ONE. Optional, and '' means they do
   * not — most project owners are a client's sponsor who will never open
   * FlightDeck. When it is set, they answer their own row first-hand instead of
   * having a manager type it for them.
   */
  projectOwnerEmail?: string;
  /**
   * WHETHER THE OWNER IS ASKED. True unless somebody says otherwise, and absent
   * means true — a board that has bothered to name an owner means it, and the
   * person who commissioned the work approving it is the ordinary case rather
   * than the configured one.
   *
   * SEPARATE FROM THE NAME on purpose. Taking them off the panel by clearing
   * the name would lose who commissioned the project in order to record that
   * they do not sign for it, and those are two different facts.
   */
  ownerSignsOff?: boolean;
  /**
   * ── WHO SIGNS, AS A RULE — #332, migration 0075 ──────────────────────────
   *
   * `signatories` above is a list of names, and a list of names is wrong the
   * day somebody leaves — silently, because the sheet still looks complete and
   * is addressed to somebody who is not here. Vinnie, 1 September 2026:
   * *"seeing that can change at any time due to changing staff"*.
   *
   * This is the rule that replaces it for anything raised from now on: ranks
   * plus anybody typed in, read into real people at the moment of asking and
   * frozen onto the sign-off. See `src/signoff-policy.ts`.
   *
   * ⚠️ `signatories` IS NOT REMOVED AND MUST NOT BE. Every release sign-off
   * already raised was frozen from it, the release flow still reads it, and a
   * board that has one and no rule keeps working exactly as it did.
   */
  /**
   * ⚠️ AS THE BOARD HAS IT, WHICH IS ANY OF THREE SHAPES — #346, and R1's
   * default+overrides on top. A board set up before the chain existed carries
   * `{roles, onSprintFinish}`; a board that has never overridden a type carries a
   * flat `StoredSignoffPolicy`; a board that has carries a `StoredSignoffConfig`
   * (`{default, overrides}`). Nothing rewrites any of them. `readPolicy`,
   * `readConfig` and `policyForType` in signoff-policy.ts are the only things that
   * read this, and they hand back the chain. Nothing else should touch it raw.
   */
  signoffPolicy?: StoredSignoffPolicy | StoredSignoffConfig;
  /**
   * WHEN THIS WORK WAS ASKED FOR — YYYY-MM-DD, or '' for not recorded. Not when
   * it started and not when a sign-off was requested; those are three different
   * dates and only this one cannot be reconstructed afterwards.
   */
  requestedOn?: string;
  /**
   * WHO SIGNS WORK OFF HERE, in the order they appear on the sheet — seniority
   * is carried by the order, so this is a list rather than a set.
   *
   * The standing panel is the DEFAULT for a new sign-off, never a live link to
   * one: a release freezes its own signatories when approval is requested, so
   * changing this never alters something already part-signed.
   */
  signatories?: Signatory[];
  /**
   * EVERYBODY WHO HAS EVER BEEN ON THAT PANEL. Taking a one-off approver off
   * the panel should not erase that they exist — next time they are picked
   * rather than retyped, and a name typed once is a name spelled one way.
   */
  signatoriesKnown?: Signatory[];
  /**
   * What must be true before anything on this project may be called done.
   *
   * One list for the whole project rather than per issue: a check somebody has
   * to remember to add is a check that is missing from the issues that most
   * needed it, and "done" quietly goes back to meaning whatever anyone wants.
   */
  doneChecks?: string[];
  /**
   * Where this project's issues come from: written on the board, or mirrored
   * from an external tracker. Absent = 'tracker', which is how this board began.
   *
   * This is a permanent description of how a team works, not a feature switch.
   * It decides which fields the board may edit, and who classifies blockers.
   */
  source?: 'board' | 'tracker';
  /**
   * What that external tracker is called, in this project's own words — a file,
   * a system, a script, whatever it happens to be. The interface says "comes
   * from {this}", so the software never has to know what any one team uses.
   */
  sourceName?: string;
  /**
   * WHETHER THIS BOARD WORKS IN TIMEBOXES, or in flow.
   *
   * Flow — the absent default and what every board does today — answers "what is
   * the single best thing to start now". Sprints answer "what is left in this
   * one". They are different questions, and a team that plans in fortnights
   * cannot get theirs answered by a board that only knows the first.
   *
   * ABSENT IS FLOW, and it has to be: every board that exists predates this, and
   * reading absent as sprints would put a sprint header on all of them at once
   * with no sprint behind it. See src/sprints.ts.
   */
  cadence?: 'builds' | 'sprints';
  /**
   * WHETHER THIS BOARD IS RUN AS A BOUNDED PROJECT OR A CONTINUOUS SERVICE — #435.
   *
   * `project` is a bounded delivery with a plan behind it — phases, milestones
   * and a go-live. `continuous` is ongoing, keep-the-lights-on work with no
   * fixed end, like a support queue. ABSENT READS AS `project`, and it has to:
   * every board predates this and ran as one, and the column defaults to
   * `project`, so nothing changes for an existing board until someone chooses
   * `continuous`. This is a different axis from whether a project is `tracked`
   * (has a plan yet). The gate helpers isProjectMode / isContinuous in
   * src/lifecycle.ts are the one place every project-shape surface (the Plan
   * tab, phases, milestones) reads it. See src/lifecycle.ts.
   */
  deliveryMode?: 'project' | 'continuous';
  /**
   * HOW THE VENDOR(S) ARE PAID — which decides how the cost side can be read.
   *
   * On a FIXED-PRICE contract the actual spend (AC) follows the agreed PAYMENT
   * schedule, not the effort, so the cost-efficiency index (CPI) reads payment
   * timing and heals toward 1.00 as the work wraps — US-federal / NASA guidance
   * discourages EVM cost analysis on firm-fixed-price for exactly this reason. On
   * TIME & MATERIALS the spend tracks effort, so CPI is a genuine efficiency read.
   * MIXED (or absent) keeps the balanced read — the estimate-at-completion vs the
   * baseline (VAC) leads, CPI supports. Project-level for v1; per-vendor contract
   * types are a later model. Read by the cost tile via src/portfolio.ts costBasisRead.
   */
  costBasis?: CostBasis;
  /**
   * HOW A NEW CARD AUTO-ASSIGNS ITS CREATOR on this board (two-part assignee, A).
   *
   * A board-wide override of each person's company-role default: `follow` (the
   * default — every creator lands in the slot their own company role implies),
   * or force the whole board to `accountable`, `responsible`, or `none` (don't
   * auto-assign). Set to `responsible` for a doer-led board such as a vendor
   * delivery board. ABSENT READS AS `follow`; the column defaults to it, so an
   * existing board is unchanged. Same board-level shape as `deliveryMode`. The
   * resolver that reads it lives in src/company-role.ts.
   */
  cardDefaults?: import('./company-role').CardStance;
  /**
   * WHETHER THIS BOARD SHOWS THE OPTIONAL "IN STAGING" COLUMN — #471.
   *
   * An extra lane between In Progress and In Review, for work that is built and
   * waiting to go onto a staging/QA environment before review. ABSENT/false
   * reads as off, and it has to: every board predates this and has no staging
   * lane, so nothing changes until an admin turns it on in Settings. When off,
   * no card can be moved to `staging` and the lane is not shown. Turning it off
   * while cards sit in staging is guided (warn + choose where they move; abort
   * if unanswered) — see FinishSprint's pattern. The helpers stagingEnabled /
   * boardColumnsFor in src/lifecycle.ts are the one place every board surface
   * reads it.
   */
  stagingEnabled?: boolean;
  /**
   * HIDE THE DONE COLUMN WHILE IN STAGING IS ON (Vinnie, 14 Oct 2026).
   *
   * With In Staging on there are five status columns; a board that would rather
   * not scroll sideways to reach Done can turn this on to drop the Done column
   * off the board while In Staging is shown. ABSENT/false reads as off — the
   * board keeps Done and scrolls to it. Only meaningful when stagingEnabled is
   * on; it hides the board COLUMN only — a card is still marked done from its own
   * status picker, and Done returns the moment In Staging is turned off. Read in
   * exactly one place: boardColumnsFor (src/lifecycle.ts).
   */
  hideDoneInStaging?: boolean;
  /**
   * SHOW THE BACKLOG AS A COLUMN ON THE BOARD (Vinnie, 30 Oct 2026; migration 0218).
   *
   * The backlog is the holding pen BEFORE To-Do and is never open/WIP work, so by
   * default it lives on its own Backlog view and off the board's columns. ABSENT/false
   * reads as off — every board predates this and is unchanged. When a board turns it on,
   * a muted, bordered, CAPPED Backlog column is shown at the FRONT (left of To-Do) so the
   * queue can be triaged in place; To-Do stays the first open bucket and the backlog is
   * still excluded from every open/WIP count (those read the statuses, not the lane list).
   * The always-on Backlog chip in the board toolbar is separate and shows regardless.
   * Read in exactly one place: boardColumnsFor (src/lifecycle.ts).
   */
  backlogOnBoard?: boolean;
  /**
   * WHO SEES THE COMPLETION ODDS ON THIS BOARD (Layer B, migration 0220).
   *
   * The release/sprint completion odds are a management read — a card's chance of making the
   * go-live, a sprint's chance of finishing. ABSENT/false — the default, and every board
   * predates this — reads as "a Project Manager and above", who runs the board and reads the
   * forecast. When an admin turns this on in Settings, the odds show to EVERYONE on the board.
   * It governs WHO SEES the read only; it never changes a number, and the app still withholds
   * one that would be dishonest. The one place every surface reads it is
   * `canSeeCompletionOdds` (src/permissions.ts). The flip is recorded in the control trail.
   */
  showOddsToEveryone?: boolean;
  /**
   * DELIVERY-SIGNAL AUDIENCES (dossier v3, migration 0225) — who sees each half of
   * the per-card Delivery signal, set by an admin in Settings → Features. The
   * SCHEDULE read (forecast + pace) and the BUDGET read (the whole project's cost)
   * are chosen separately: `'off'` hides it, `'managers'` shows it to leads and
   * managers (the steering tier), `'all'` shows it to everyone on the board. Budget
   * can never be wider than Schedule. An external vendor never sees either; an
   * Executive always counts as a manager. Resolved only through
   * `src/signal-audience.ts`. `deliverySignalEnabled` / `costInSignal` are the
   * superseded 0222 booleans, kept readable so a not-yet-migrated board still works
   * (the audience derives from them until 0225 backfills). Changes are recorded in
   * the control trail (0225).
   */
  scheduleSignalAudience?: 'off' | 'managers' | 'all';
  budgetSignalAudience?: 'off' | 'managers' | 'all';
  /** @deprecated superseded by scheduleSignalAudience (0225); read for back-compat only. */
  deliverySignalEnabled?: boolean;
  /** @deprecated superseded by budgetSignalAudience (0225); read for back-compat only. */
  costInSignal?: boolean;
  /**
   * THE GO-LIVE -> IN-SERVICE HANDOFF — #435 surface 2, migration 0192.
   *
   * `actualGoLive` is the real go-live moment, frozen when a project is marked
   * live and moved to service (the planned date lives on the project definition as
   * `goLive`). Its presence is what makes the board read "in service" — see
   * src/lifecycle.ts and src/handoff.ts. Absent until the handoff; editable at
   * confirm, so never auto-stamped. `deliverySnapshot` is the as-delivered record
   * frozen at the same instant. Both survive a revert to project (the record is the
   * truth of what shipped). Listed in the meta assembly (store.ts) or they save and
   * then vanish on the next snapshot (#332).
   */
  actualGoLive?: number;
  deliverySnapshot?: DeliverySnapshot;
  /**
   * STRICT SEPARATION OF DUTIES FOR SCOPE CHANGES (#435 A, Vinnie 8 Oct 2026).
   *
   * A contract-value change is gated to the designated approver or an admin/manager
   * standing in. Off (the default, absent) a stand-in is ALLOWED but recorded as a
   * control exception. On, scope approval is RESERVED to the designated approver —
   * a stand-in is hard-blocked. An admin setting; the flip itself is audited. This
   * is NOT the maker-checker model (Vinnie declined that): the single-action dialog
   * stays, this only narrows who may operate it.
   */
  requireSeparateScopeApprover?: boolean;
  /**
   * HARD FINISH-TO-START DEPENDENCY BLOCK (project-card-dependencies-precedence).
   *
   * Off (the default, absent) the start-gate is SOFT: starting a card whose
   * predecessor is not done only WARNS and records an override. On, it is a true
   * block — the card cannot start until the work it waits on is finished, with no
   * override. An admin-only setting; the flip itself is recorded in the board's
   * control trail. Read by askStartGate at all three deliberate-start surfaces.
   */
  depHardBlock?: boolean;
  /**
   * WHETHER THE CARD ASSISTS SHOW ON THIS BOARD (project-idle-nudge).
   *
   * On — the default, and absent reads as on — a card quietly suggests a size and
   * a due date when it can read one from similar cards on the board: a line under
   * the field, never a block, and always ignorable. An admin can turn the assists
   * off for a board that does not want them. The flip is recorded in the board's
   * control trail (0205). Read at the card modal's size and date fields.
   */
  cardAssists?: boolean;
  /**
   * WHETHER THE DATA-QUALITY PANEL SHOWS ON THIS BOARD (project-idle-nudge).
   *
   * On — the default, and absent reads as on — Insights carries the Data quality
   * panel: the board-wide roll-up of the gaps the board's own numbers lean on.
   * Everyone on the board sees it; only a manager acts on its bulk fixes. An admin
   * can turn the panel off for a board. The flip is recorded in the board's
   * control trail (0205). Read where the panel mounts on Insights.
   */
  dataQualityPanel?: boolean;
  /**
   * HOW LONG A CARD MAY SIT BEFORE ITS IDLE NUDGE, tuned per board (0213).
   *
   * Null/absent reads as the app defaults (DEFAULT_IDLE_WINDOWS — S 3 / M 5 / L 10
   * working days, 5 for an unsized card). A board can override with its own
   * `{ bySize: { s, m, l }, noSize }`. Admin-only to change (configureAssists); the
   * change is recorded in the board's control trail. Read by the idle nudge and the
   * Insights Data quality panel.
   */
  idleWindows?: IdleWindows | null;
  /**
   * WHETHER A CHECKLIST MAY MOVE A CARD ALONG, tuned per board (0214).
   *
   * Null/absent reads as the app defaults (DEFAULT_CHECKLIST_PROMPTS — everything
   * on). A `{ on, onFirstTick, onComplete }` shape: `on` is the master, and the two
   * flags pick which offers are made — a To-do card's first tick offering to start
   * it, a 100% checklist offering to finish it. `onComplete` also governs whether a
   * full checklist clears the Done gate in place of the board DoD. Admin-only to
   * change (configureAssists); the change is recorded in the board's control trail.
   * Read by the card modal's checklist and by `doneAllowed` (done.ts).
   */
  checklistPrompts?: ChecklistPrompts | null;
  /**
   * WHETHER THE TOOL HOLDS THE PEN ON THE VERSION NUMBER.
   *
   * Vinnie, 15 August 2026: *"an option (a switch) that allows an assistant
   * driven board to control the build and version number. Once the switch is
   * off, it works as you recommended."*
   *
   * Off: a person types the version when they cut a release, and the software
   * only ever suggests. On: the version and build number arrive with the push,
   * from software that already has a version of its own — and FlightDeck
   * recording a second opinion is two numbers that will eventually disagree
   * with nothing to say which was right.
   *
   * NOT THE SAME AS `keptByAssistant` BELOW, which looks like the same fact and
   * is not: that one is stamped by the tool and records what is happening, this
   * one is a decision a person makes about who is in charge.
   */
  assistantVersions?: boolean;
  /**
   * Whether an assistant keeps this board — stamped by the tool that pushes to
   * it, not ticked by a person. A claim that one is involved proves nothing; a
   * board actually being written to by one is the fact worth acting on.
   *
   * Absent on every board nobody has pushed to that way, which is most of them,
   * and the assistant settings simply do not appear there.
   */
  keptByAssistant?: boolean;
  /**
   * Whether that assistant may read and answer comments here. Off until the
   * person whose board this is says otherwise, and theirs to withdraw.
   *
   * See src/assistant.ts for what this does and does not permit.
   */
  assistantMayAnswer?: boolean;
  /**
   * Every assistant that has worked on this board, and what each may do.
   *
   * Keyed by the name the tool gives for itself. Entries appear by being used,
   * not by being added: a tool that touches this board records itself here, and
   * that is what makes it manageable. Nobody has to know in advance what will
   * turn up, and nothing can be permitted that has never been seen.
   *
   * Permission is per assistant, because "an AI may read the comments" is not
   * one decision when two different ones are working on the same project.
   */
  assistants?: Record<string, AssistantEntry>;
  /**
   * When somebody last went through the setup questions for this project.
   *
   * A flag rather than a guess at whether the other fields look filled in:
   * inferring it means clearing your done-checks makes the prompt reappear as
   * though the project had reset. Set by finishing OR by skipping — being
   * nagged is worse than missing a prompt, and Settings always has the way back.
   */
  setUpAt?: number;
  /**
   * WHETHER WHAT THIS PROJECT MAKES GETS INSTALLED ON THINGS.
   *
   * Vinnie, 15 August 2026: *"This app is not only meant for phones, but for
   * desktop software as well… if I am developing a SaaS then there will be no
   * recorded device… Off by default, and a list with a custom option."*
   *
   * OFF IS THE DEFAULT AND THE COMMON CASE. A web product is released, not
   * installed: nothing carries it, and there is no list to keep. Every release
   * on such a board used to say *"Phones not recorded"* for ever, which reads as
   * a record somebody failed to keep rather than a question that does not apply.
   * Off, the line does not appear at all.
   *
   * NOT THE SAME QUESTION AS `assistantVersions`, and it must not be answered by
   * it. That one asks who holds the pen; this one asks what the project is. A
   * tool-driven board can ship a phone app, and a hand-run board can ship a
   * website — see src/tracker-format.ts, where a tool reporting devices is told
   * it may add to the list and never clear it.
   */
  tracksDevices?: boolean;
}

export interface AssistantEntry {
  /** How it should read on screen. Falls back to the key. */
  name?: string;
  /**
   * When a person added it here. Distinct from lastSeen on purpose: added and
   * actually working are different facts, and a list that showed only the first
   * would let something sit there looking connected while nothing ever ran.
   */
  addedAt?: number;
  /** When it last did anything here, so a stale entry is obvious. */
  lastSeen?: number;
  /** Whether this one may read and answer comments. Off until someone says so. */
  mayAnswer?: boolean;
  /**
   * THE ACCOUNT ISSUED FOR IT, IF ONE WAS.
   *
   * Without this the two halves of an assistant are only joined by somebody
   * remembering: a register entry called `claude-cli` and an account called
   * `claude-cli@somewhere` are, to the software, unrelated. Which meant
   * removing an assistant could take the entry away and leave the credentials
   * working — a revocation that revokes nothing, and the worst possible half of
   * the job to do silently.
   *
   * Absent on entries made before this was recorded, and on assistants that
   * were never given an account because a person does their pushing. Removal
   * says which of those it is looking at rather than guessing.
   */
  email?: string;
  /**
   * WHO PUTS THE TRACKER ON THE BOARD — the question the screen used to answer
   * for you, wrongly, and then could not remember the answer to.
   *
   * 'person'    — you push the file yourself while signed in. The assistant
   *               never touches the board, so it needs no account.
   * 'assistant' — it signs in and writes here itself, which means it needs one.
   *
   * ABSENT MEANS UNANSWERED, and that is a third state rather than a missing
   * value to be defaulted. Guessing either way is exactly the fault this
   * replaced: guess 'person' and somebody is told they have finished three
   * steps early; guess 'assistant' and they are pushed into issuing
   * credentials nothing will ever use.
   */
  pusher?: 'person' | 'assistant';
}

export const STATUS_LABELS: Record<IssueStatus, string> = {
  backlog: 'Backlog',
  todo: 'To Do',
  inprogress: 'In Progress',
  staging: 'In Staging',
  inreview: 'In Review',
  done: 'Done',
  wontdo: "Won't Do",
};

/**
 * The word the ACTUAL-dates line uses for a card that carries a real START date,
 * no FINISH date yet, AND is NOT currently being worked — "Started 4 Oct · on
 * hold". The actuals line is read in step with the board lane (Vinnie, 5 Oct):
 *   · While the card is actively being worked — status In Progress or In Review —
 *     the line shows ONLY "Started {date}". The status badge already says the work
 *     is live, so no running word is added, and none can contradict the lane. (The
 *     old "Started 4 Oct · in progress", then "· underway", read as a flat
 *     contradiction on a card sitting in "To Do": a word-swap left the contradiction
 *     in place, so the rule is now to obey the lane, not to rename the word.)
 *   · Once the card has been moved BACK to To Do / Backlog — started once, then set
 *     aside — the line shows "Started {date} · on hold", honest to the lane rather
 *     than claiming it is still running. This is the only place the word appears.
 * Constraints, guarded by test-actuals-copy-avoids-status-words.ts so they cannot
 * regress: it must never be one of the STATUS_LABELS (the lane-collision above); it
 * must not be a BLOCKER_LABEL word such as "parked" (that names a *blocked reason*,
 * a different thing a card shows); and it must not contain "finished" (a started-
 * but-unfinished card must never imply a completion — rules out "unfinished" too). */
export const ACTUAL_PARKED_LABEL = 'on hold';

export const STATUS_ORDER: IssueStatus[] = ['backlog', 'todo', 'inprogress', 'staging', 'inreview', 'done', 'wontdo'];

/**
 * THE PROCESS STARTS AT TO-DO (Vinnie, 3 Nov 2026). The backlog is a dumping ground
 * of ideas — a card there is not yet in anyone's process, so it is never owed a plan
 * and must not appear in any attention / health / behind / blocked / overdue count or
 * list. The ONE metric that counts the backlog is the backlog-growth insight (the net
 * backlog-change trend). Every other count runs over `isInProcess`: To-Do up to In
 * Review, with the backlog parked out and the resolved (done/wontdo) excluded as ever.
 */
export const isBacklog = (s: IssueStatus): boolean => s === 'backlog';
export const isInProcess = (s: IssueStatus): boolean => s !== 'backlog' && s !== 'done' && s !== 'wontdo';
// The board's visible lanes WHEN In Staging is off — the default every board has.
// `staging` is opt-in, so it is spliced in per-board by boardColumnsFor() (lifecycle.ts)
// rather than living here; a board that never enables it behaves exactly as today.
export const BOARD_COLUMNS: IssueStatus[] = ['todo', 'inprogress', 'inreview', 'done'];

export const PRIORITY_LABELS: Record<IssuePriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/**
 * A mark beside the word, so priority is never carried by colour alone.
 *
 * Medium has none on purpose. It is the default and by far the most common, so a
 * symbol on every card would be noise — and "no arrow" reads as the middle of a
 * scale whose ends both point somewhere. The word is always there regardless;
 * these are read WITH it, never instead of it.
 *
 * CRITICAL IS A FILLED SHAPE, NOT A BIGGER ARROW.
 *
 * It shipped as ⇈, the paired-arrow glyph, which most fonts draw thin — at 11px
 * it read as a slightly fat ↑ and was reported as illegible the first time
 * somebody looked at it. Enlarging it did not help; the glyph is the problem.
 *
 * A filled triangle differs from High in SHAPE rather than in size or count,
 * which is the difference that survives being small, being grey, and being
 * glanced at. Compared against ⇈ and ↑↑ at the real 11.5px, in both themes and
 * with the colour stripped out, before this was chosen.
 */
export const PRIORITY_SYMBOL: Record<IssuePriority, string> = {
  critical: '\u25b2',
  high: '\u2191',
  medium: '',
  low: '\u2193',
};

export const PRIORITY_ORDER: IssuePriority[] = ['critical', 'high', 'medium', 'low'];

/**
 * THE MoSCoW SCALE IN PLAIN WORDS — no acronym on screen (that is jargon), the
 * values name themselves. The long labels are what a person picks from; the
 * short ones are the chip a requirements document wears.
 */
export const MOSCOW_LABELS: Record<MoscowLevel, string> = {
  must: 'Must have',
  should: 'Should have',
  could: 'Could have',
  wont: "Won't have",
};
export const MOSCOW_SHORT: Record<MoscowLevel, string> = {
  must: 'Must',
  should: 'Should',
  could: 'Could',
  wont: "Won't",
};
/* Most-essential first, the order a requirements list is read and rolled up in. */
export const MOSCOW_ORDER: MoscowLevel[] = ['must', 'should', 'could', 'wont'];

/**
 * What an empty column should say.
 *
 * "Drop cards here" describes the mechanism and tells you nothing. An empty
 * column is usually good news — nothing stuck in review, nothing waiting — and
 * the wording should say which column is empty rather than leave the reader to
 * work it out from where the box sits.
 *
 * Written per status because the generic form does not survive contact with the
 * English: "Nothing in To do" and "Nothing in Won't do" both read badly.
 */
export const EMPTY_COLUMN: Record<IssueStatus, string> = {
  backlog: 'Nothing in the backlog.',
  todo: 'Nothing waiting to start.',
  inprogress: 'Nothing in progress.',
  staging: 'Nothing in staging.',
  inreview: 'Nothing waiting to be reviewed.',
  done: 'Nothing finished yet.',
  wontdo: 'Nothing set aside.',
};
